import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";
import AddressInput from "@/shared/components/AddressInput";
import AuthDialog from "@/shared/components/AuthDialog";
import RouteMap from "@/shared/components/RouteMap";
import SlideToConfirm from "@/shared/components/SlideToConfirm";
import StripeCheckoutSheet from "@/shared/components/StripeCheckoutSheet";
import BookingSheet from "@/apps/rider/components/booking/BookingSheet";
import MapGreeting from "@/apps/rider/components/MapGreeting";
import PriceOdometer from "@/shared/components/PriceOdometer";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, ArrowRight, MapPin, X, Check, Sparkles, Sun, Moon,
  Crown, Calendar as CalendarIcon, ChevronDown,
} from "lucide-react";
import { useReducedMotion } from "framer-motion";
import { sortDays, formatTime12, formatPickupWindow } from "@/shared/lib/format";

/**
 * /schedule — mobile-first 4-step booking experience.
 *
 *   0  Route Confirmation (map-dominant)
 *   1  Frequency + Days
 *   2  Time Window
 *   3  Review + Pay
 *
 * UI/UX layer ONLY. Pricing, availability, route calculation, and Stripe checkout
 * are unchanged — every step writes into the same state shape the API expects.
 */
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Step registry — the wizard reads its length, labels, and ids from this list.
 * Insert a new step anywhere by adding an entry and a matching `currentId === 'foo'`
 * render branch; navigation logic, progress bar, and history all adapt automatically.
 */
const STEPS = [
  { id: "route", label: "Route" },
  { id: "plan",  label: "Plan & Pay" },
];
// Time picker constants — simple hour / minute / AM-PM wheels.
const HOUR_OPTIONS   = Array.from({ length: 12 }, (_, i) => {
  const n = i + 1;
  return { id: String(n), label: String(n) };
});
const MINUTE_OPTIONS = [
  { id: "00", label: ":00" },
  { id: "15", label: ":15" },
  { id: "30", label: ":30" },
  { id: "45", label: ":45" },
];
const AMPM_OPTIONS = [
  { id: "AM", label: "AM" },
  { id: "PM", label: "PM" },
];

function pad2(n) { return String(n).padStart(2, "0"); }


/** "HH:MM" 24-hour → { hour12, minute, ampm } for the tri-wheel UI. */
function from24h(hhmm) {
  const [h, m] = (hhmm || "08:00").split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour12 = ((h + 11) % 12) + 1; // 0→12, 1→1, …, 12→12, 13→1
  // snap to nearest 15-min slot
  const minute = pad2((Math.round(m / 15) * 15) % 60);
  return { hour12, minute, ampm };
}

/** { hour12, minute, ampm } → "HH:MM" 24-hour. */
function to24h(hour12, minute, ampm) {
  let h = hour12 % 12;       // 12 → 0
  if (ampm === "PM") h += 12;
  return `${pad2(h)}:${minute}`;
}

function subMinutes(hhmm, mins) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(2000, 0, 1, h, m);
  d.setMinutes(d.getMinutes() - mins);
  return d.toTimeString().slice(0, 5);
}

/** Return user's current weekday in their local timezone as a Mon/Tue/.../Sun string. */
function localWeekdayKey() {
  const d = new Date();
  // JS getDay(): 0=Sun … 6=Sat. DAYS array starts Mon.
  return DAYS[(d.getDay() + 6) % 7];
}

/** Top progress: dot indicator + step label */
function StepProgress({ currentIdx, total, label }) {
  return (
    <div className="px-4 sm:px-6 pt-3 pb-2" data-testid="wizard-stepbar">
      <div className="flex items-center justify-between text-[10px] tracking-widest uppercase text-[#515E7B]">
        <span data-testid="wizard-step-label">Step {currentIdx + 1} of {total} · {label}</span>
        <span className="text-[#515E7B]/70">{Math.round(((currentIdx + 1) / total) * 100)}%</span>
      </div>
      <div className="mt-2 flex items-center gap-1.5" aria-hidden="true">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 transition-all duration-300 ${i <= currentIdx ? "bg-[#ed6500]" : "bg-[#E5E1D8]"}`}
            data-testid={`wizard-step-pip-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function ScheduleSetup() {
  const nav = useNavigate();
  const location = useLocation();
  const [sp, setSp] = useSearchParams();
  const { user, loading } = useAuth();

  // Heuristic for "this string is a fully-formed address" — used by both
  // the auto-calc effect and the sheet-state machine to gate transitions
  // out of the addressing pill. Autocomplete suggestions always contain
  // a comma ("Street, City, ST ZIP"); partial typing rarely does.
  const looksLikeAddress = (s) => {
    if (!s) return false;
    const t = s.trim();
    return t.includes(",") && t.length >= 12;
  };

  // ---------- state (unchanged shape — keeps backend integration intact) ----------
  const [step, setStep] = useState(0);
  const [pickup, setPickup] = useState("");
  const [destination, setDestination] = useState("");
  // Address-autocomplete dropdown state lifted to ScheduleSetup so the
  // route-quote auto-calc can wait until BOTH dropdowns are closed —
  // otherwise the cinematic "Tracing route" preview kicks in 600ms
  // after the user starts typing and they get cut off mid-address.
  const [pickupDropdownOpenTop, setPickupDropdownOpenTop] = useState(false);
  const [destDropdownOpenTop, setDestDropdownOpenTop] = useState(false);
  const [days, setDays] = useState([]);
  // Window-first scheduling — the rider picks a 15-minute PICKUP window
  // and a 15-minute RETURN window directly inside the route card.
  // Defaults (spec A2.2): pickup 7:00–7:15 AM, return 5:00–5:15 PM —
  // the two most common commute windows. NEVER 12:00 AM.
  const [pickupWindow, setPickupWindow] = useState("07:00-07:15");
  const [returnWindow, setReturnWindow] = useState("17:00-17:15");
  const [seats, setSeats] = useState(1);                // 1..5 (5 = "5+")
  const [pref, setPref] = useState("shared");           // "shared" | "private"
  const [arrival, setArrival] = useState("08:00");
  const [ret, setRet] = useState("17:00");
  const [dayOverrides, setDayOverrides] = useState({}); // { Mon: { arrival_time, return_time }, ... }
  const [buffer, setBuffer] = useState(30);
  const [distance, setDistance] = useState(0);
  const [duration, setDuration] = useState(0);
  const [polyline, setPolyline] = useState("");
  const [cycle, setCycle] = useState("weekly");
  // `cycleUserPicked` lives at the top level (not inside PlanStep) so it
  // survives step transitions.  When user explicitly taps One-time /
  // Subscription in the Review step we flip this to `true` and the
  // auto-default logic stops re-snapping to weekly.
  //
  // ALSO consumed by the new flat BookingSheet (the legacy PlanStep
  // wizard no longer renders) so the smart 1-day-=-one-time default
  // works regardless of which surface the rider is in.
  const [cycleUserPicked, setCycleUserPicked] = useState(false);
  // Top-level smart default — replicates the rule that used to live
  // inside the legacy PlanStep wizard (1 day → one-time, 2+ days →
  // weekly subscription). Without this, the BookingSheet's day count
  // was never reflected on `cycle` and EVERY rider got forced into a
  // weekly subscription (no one-time payment possible). Once the rider
  // taps the toggle in the BookingSheet we flip `cycleUserPicked` and
  // never re-snap.
  useEffect(() => {
    if (cycleUserPicked) return;
    if (days.length <= 1 && cycle !== "payg") setCycle("payg");
    else if (days.length >= 2 && cycle === "payg") setCycle("weekly");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days.length, cycleUserPicked]);
  const [tiers, setTiers] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [tierLocked, setTierLocked] = useState(false);
  // Entry override — set true when the rider swipes down from a wizard
  // step (or taps Back on Step 0) to return to the "Where to?" address
  // entry surface WITHOUT unlocking the tier. This preserves the
  // polyline draw + wizard state so a subsequent swipe-up returns
  // straight to the step they left. Cleared on swipe-up.
  const [entryOverride, setEntryOverride] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const [maxMiles, setMaxMiles] = useState(50);
  const [firstRide, setFirstRide] = useState({ eligible: false, welcome_rate_bucket: null, discount_pct: 0, discount_type: null });
  // Map-first: capture user's geolocation for the empty-state map preview and
  // for biasing places autocomplete. Falls back to Kansas City if denied/unavailable.
  const KC_FALLBACK = { lat: 39.0997, lng: -94.5786 };
  const [userLoc, setUserLoc] = useState(KC_FALLBACK);
  const [hasUserLoc, setHasUserLoc] = useState(false);
  const [cancelledBanner, setCancelledBanner] = useState(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("cancelled") === "1";
  });

  // After payment / activation, the rider has at least one upcoming trip
  // leg. We poll /api/me/trip/upcoming every 30s (paused when the tab is
  // hidden) so the bottom-bar's idle state can morph into a "current leg"
  // pill instead of the generic "Set your route to begin" hint. When all
  // legs are completed/cancelled the endpoint returns {trip:null} and the
  // surface naturally reverts to the original idle state.
  const [upcomingTrip, setUpcomingTrip] = useState(null);
  // "Repeat last week" — set when the home card's Repeat CTA seeds
  // the preset with jumpToPay=true. Triggers an auto-getTier once
  // the addresses are hydrated so the wizard lands directly on
  // Review & Pay (idx 2).
  const [jumpToPay, setJumpToPay] = useState(false);
  const initialWizardStepIdx = jumpToPay ? 2 : 0;
  useEffect(() => {
    if (!user) { setUpcomingTrip(null); return; }
    let stopped = false;
    const tick = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const { data } = await api.get("/me/trip/upcoming");
        if (!stopped) setUpcomingTrip(data?.trip || null);
      } catch (_) { /* noop */ }
    };
    tick();
    const id = setInterval(tick, 30_000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user]);

  useEffect(() => {
    if (sp.get("cancelled") === "1") {
      toast.info("Payment canceled — no charge was made. Your draft is ready when you are.");
      const next = new URLSearchParams(sp); next.delete("cancelled"); setSp(next, { replace: true });
    }
  }, [sp, setSp]);

  // Preset hydration — when a prospect priced a route via the /welcome
  // instant-quote chip we stash {pickup, destination, ts} in localStorage
  // under `link.preset.route`. On first mount of /schedule we consume
  // that preset, prefill the booking pill, and clear the key. Bounded
  // to 30 min so a forgotten session can't overwrite a deliberate clear
  // days later.
  const consumedPresetRef = useRef(false);
  useEffect(() => {
    if (consumedPresetRef.current) return;
    consumedPresetRef.current = true;
    // C2 · Two-source hydration:
    //   1. link.preset.route (single-shot: welcome instant-quote or
    //      AuthCallback resume) — consume + delete after read.
    //   2. link.wizard.draft (auto-saved on every state change while
    //      the rider is in the wizard) — persistent until checkout.
    // We read (1) first because it may contain freshly-restored data
    // from a Google-sign-in bounce; (2) is a fallback for tab-bar
    // SIGN IN paths that don't go through AuthGoogleButton.
    let raw = null;
    try { raw = localStorage.getItem("link.preset.route"); } catch (_) { /* noop */ }
    if (!raw) {
      try { raw = localStorage.getItem("link.wizard.draft"); } catch (_) { /* noop */ }
    }
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      const ageMs = Date.now() - Number(parsed?.ts || 0);
      if (ageMs > 30 * 60 * 1000) {
        try { localStorage.removeItem("link.preset.route"); } catch (_) { /* noop */ }
        try { localStorage.removeItem("link.wizard.draft"); } catch (_) { /* noop */ }
        return;
      }
      if (parsed?.pickup) setPickup(parsed.pickup);
      if (parsed?.destination) setDestination(parsed.destination);
      // C2 · If the preset was written by AuthCallback to restore a
      // mid-booking session, also rehydrate the wizard fields so the
      // rider lands back on Step 1 with everything as they left it.
      if (Array.isArray(parsed?.days) && parsed.days.length) setDays(parsed.days);
      if (parsed?.pickupWindow) setPickupWindow(parsed.pickupWindow);
      if (parsed?.returnWindow) setReturnWindow(parsed.returnWindow);
      if (Number.isFinite(parsed?.seats)) setSeats(parsed.seats);
      if (parsed?.pref === "shared" || parsed?.pref === "private") setPref(parsed.pref);
      if (parsed?.cycle === "payg" || parsed?.cycle === "weekly") { setCycle(parsed.cycle); setCycleUserPicked(true); }
      // "Repeat last week" preset — jump the wizard straight to
      // Review & Pay (idx 2) and force-lock the tier so the sheet
      // lands in `ready` state with no extra taps.
      if (parsed?.jumpToPay === true) {
        setJumpToPay(true);
      }
    } catch (_) { /* malformed JSON, ignore */ }
    try { localStorage.removeItem("link.preset.route"); } catch (_) { /* noop */ }
  }, []);

  // C2 · Auto-save wizard state so a rider mid-booking can go to /login
  // (Google OR email/password), sign in, and return with everything
  // preserved. Cleared explicitly on /checkout/plan navigation.
  useEffect(() => {
    if (!pickup && !destination && (!days || !days.length)) return;
    try {
      localStorage.setItem("link.wizard.draft", JSON.stringify({
        pickup, destination,
        days, pickupWindow, returnWindow,
        seats, pref, cycle,
        ts: Date.now(),
      }));
    } catch (_) { /* private-mode / storage full */ }
  }, [pickup, destination, days, pickupWindow, returnWindow, seats, pref, cycle]);

  // Auto-prefill pickup with the rider's saved home address (iter 67).
  //
  // Runs at most once per signed-in session. We only prefill when:
  //   • the pickup field is still empty (don't clobber what the rider has typed)
  //   • the rider has actually saved a home_address
  //   • we haven't already prefilled this mount (so resetToken-driven wipes
  //     don't keep re-injecting it after a deliberate clear)
  //
  // Gives onboarded riders an "it knows me" moment on their very first
  // booking and shaves ~10s off every subsequent route entry.
  const prefilledFromHomeRef = useRef(false);
  useEffect(() => {
    if (prefilledFromHomeRef.current) return;
    if (!user || !user.home_address) return;
    if (pickup) { prefilledFromHomeRef.current = true; return; }
    // If a preset was already consumed and seeded a pickup, don't
    // overwrite it with the saved home address.
    if (consumedPresetRef.current && pickup) { prefilledFromHomeRef.current = true; return; }
    setPickup(user.home_address);
    prefilledFromHomeRef.current = true;
  }, [user, pickup]);

  // Logo / Home tab reset hook — when the user taps the brand logo or the
  // Home tab, the navigator pushes us back to /schedule with a fresh
  // location.state.reset timestamp. We listen and wipe the wizard state so
  // riders always land on a clean Step 1 (map / route entry screen).
  const resetToken = location.state?.reset;
  useEffect(() => {
    if (!resetToken) return;
    setStep(0);
    setPickup("");
    setDestination("");
    setDays([]);
    setArrival("08:00");
    setRet("17:00");
    setPickupWindow("07:00-07:15");
    setReturnWindow("17:00-17:15");
    setSeats(1);
    setPref("shared");
    setDayOverrides({});
    setBuffer(30);
    setDistance(0);
    setDuration(0);
    setPolyline("");
    setTierLocked(false);
    setUnsupported(false);
    // Drop the marker from history so a back-press doesn't re-fire the reset.
    nav(location.pathname, { replace: true, state: {} });
  }, [resetToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/pricing").then((r) => {
      setTiers(r.data.tiers || []);
      setCycles(r.data.billing_cycles || []);
      setMaxMiles(r.data.max_one_way_miles || 50);
    });
  }, []);

  // Geolocation on mount — instant feedback for the map-first booking screen.
  // Quiet failure: if denied or unsupported, we already have the KC fallback set.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setHasUserLoc(true);
      },
      // No toast on denial — graceful fallback is the better UX
      () => {},
      { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: 6000 }
    );
    return () => { cancelled = true; };
  }, []);

  // Keyboard-aware body lock — when an input/textarea inside the schedule
  // wrapper is focused, pin <body> via `body.kbd-open` so iOS Safari can't
  // elastic-scroll the entire layout. Combined with the
  // `interactive-widget=resizes-content` viewport meta this means: keyboard
  // opens → only the visible viewport shrinks, the map + bottom nav stay
  // anchored, only the focused input area stays in view.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onFocusIn = (e) => {
      const t = e.target;
      if (!t) return;
      if (t.matches && t.matches("input, textarea, select, [contenteditable='true']")) {
        document.body.classList.add("kbd-open");
      }
    };
    const onFocusOut = () => {
      document.body.classList.remove("kbd-open");
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      document.body.classList.remove("kbd-open");
    };
  }, []);

  useEffect(() => {
    if (!user) { setFirstRide({ eligible: false, welcome_rate_bucket: null, discount_pct: 0, discount_type: null }); return; }
    api.get("/pricing/first-ride")
      .then((r) => setFirstRide({
        eligible: !!r.data.eligible,
        welcome_rate_bucket: r.data.welcome_rate_bucket || null,
        discount_pct: Number(r.data.discount_pct || 0),
        discount_type: r.data.discount_type || null,
      }))
      .catch(() => setFirstRide({ eligible: false, welcome_rate_bucket: null, discount_pct: 0, discount_type: null }));
  }, [user]);

  // ---------- derived ----------
  const pickupTime = useMemo(() => subMinutes(arrival, buffer), [arrival, buffer]);

  // Window-first → exact-time projection.
  //
  // Backend contract is arrival_time + buffer_minutes; the rider chooses
  // PICKUP windows directly inside the route card. We project:
  //   arrival_time = pickup_window_start + ride_duration + small grace
  //   buffer_minutes = ride_duration + grace
  //   → backend recomputes pickup_time = arrival - buffer = window_start ✓
  // and similarly the return leg's `ret` (return arrival) is derived from
  // the return_window so the backend records a coherent route.
  useEffect(() => {
    if (!pickupWindow) return;
    const [hh, mm] = pickupWindow.slice(0, 5).split(":").map((n) => parseInt(n, 10));
    if (Number.isNaN(hh) || Number.isNaN(mm)) return;
    const dur = Math.max(10, Math.round(Number(duration) || 25));
    const grace = 5;
    const total = dur + grace;
    const startMin = hh * 60 + mm + total;
    const newH = Math.floor(startMin / 60) % 24;
    const newM = startMin % 60;
    const pad = (n) => String(n).padStart(2, "0");
    setArrival(`${pad(newH)}:${pad(newM)}`);
    setBuffer(total);
  }, [pickupWindow, duration]);

  useEffect(() => {
    if (!returnWindow) return;
    const [hh, mm] = returnWindow.slice(0, 5).split(":").map((n) => parseInt(n, 10));
    if (Number.isNaN(hh) || Number.isNaN(mm)) return;
    setRet(`${pad2(hh)}:${pad2(mm)}`);
  }, [returnWindow]);

  const currentTier = useMemo(() => {
    if (!tiers.length || !tierLocked || unsupported) return null;
    const miles = Number(distance) || 0;
    if (miles <= 8) return tiers.find((t) => t.id === "local");
    if (miles <= 16) return tiers.find((t) => t.id === "city");
    if (miles <= 32) return tiers.find((t) => t.id === "commute");
    return tiers.find((t) => t.id === "extended");
  }, [distance, tiers, tierLocked, unsupported]);

  // ---- Server-driven pricing.
  //
  // The pricing matrix (base price, per-mile rate, discount ladder) is
  // confidential and intentionally NOT shipped to the client. We call
  // /api/pricing/quote with (distance, days/week) and the backend
  // returns the final daily/weekly numbers. A debounced effect keeps
  // the quote fresh as the rider's distance or day-count changes.
  //
  // We always send `days = max(1, days.length)` so we get a valid
  // daily_price even before the rider has picked any days (so the
  // route-confirmation preview shows a real $/day number). Weekly
  // total is only meaningful once at least one day is selected.
  const [quote, setQuote] = useState({ daily_price: 0, weekly_total: 0, distance_miles: 0, days: 0, fare: null });
  useEffect(() => {
    const miles = Number(distance) || 0;
    const n = Math.max(1, days.length || 1);
    if (!tierLocked || unsupported || miles <= 0) {
      setQuote({ daily_price: 0, weekly_total: 0, distance_miles: miles, days: days.length, fare: null });
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/pricing/quote", {
          // Feb 2026 rebuild — pass seats + ride_type + billing_cycle
          // so the server-computed fare bundle matches what /pay/intent
          // will charge. Without these params the price odometer would
          // show 1-rider-payg while the PaymentIntent charges the
          // 2-rider-subscribe fare (or vice versa) → P0 mismatch.
          params: {
            distance_miles: miles,
            days: n,
            seats: Math.max(1, Number(seats || 1)),
            ride_type: pref || "shared",
            billing_cycle: cycle || "payg",
          },
        });
        if (cancelled) return;
        if (data?.supported) {
          setQuote({
            daily_price: Number(data.daily_price || 0),
            weekly_total: Number(data.weekly_total || 0),
            distance_miles: Number(data.distance_miles || miles),
            days: Number(data.days || n),
            fare: data.fare || null,
          });
        } else {
          setQuote({ daily_price: 0, weekly_total: 0, distance_miles: miles, days: n, fare: null });
        }
      } catch (_) {
        if (!cancelled) setQuote({ daily_price: 0, weekly_total: 0, distance_miles: miles, days: n, fare: null });
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [distance, days.length, tierLocked, unsupported, seats, pref, cycle]);

  const dailyRate = useMemo(() => Number(quote.daily_price || 0), [quote.daily_price]);
  // Fare bundle from the server — the client should always prefer these
  // values over any locally-recomputed multiplication. Legacy fields
  // (weeklyPrice / cycleTotal) are kept as fallbacks for the price
  // odometer while the fare bundle is loading.
  const fare = quote.fare || null;
  // Weekly total is only meaningful when the rider has actually picked
  // days. When days.length === 0 we asked the backend for a days=1
  // quote (to populate dailyRate) but the rider hasn't committed to a
  // weekly cadence yet, so surface 0 to the UI.
  const weeklyPrice = useMemo(
    () => (days.length > 0 ? Number(quote.weekly_total || 0) : 0),
    [quote.weekly_total, days.length],
  );
  const currentCycle = useMemo(() => cycles.find((c) => c.id === cycle), [cycle, cycles]);
  const cycleTotal = useMemo(() => {
    if (!currentTier || !currentCycle || days.length === 0) return 0;
    // Prefer the server's fare.cycle_total when available — includes
    // party × private × volume × subscribe × floor × first-ride.
    if (fare && Number(fare.cycle_total) > 0) return Number(fare.cycle_total);
    return Number((weeklyPrice * currentCycle.weeks).toFixed(2));
  }, [currentTier, currentCycle, days.length, weeklyPrice, fare]);

  const welcomeDailyRate = dailyRate;
  const firstRideDiscount = useMemo(() => {
    // Feb 2026 rebuild — the server's fare bundle is authoritative.
    // If `first_ride_wins` is TRUE the discount is already baked into
    // fare.cycle_total; we surface the SAVINGS AMOUNT here purely for
    // the Review step's informational badge (never subtract it again
    // downstream). If FALSE, no first-ride discount applies. The
    // legacy fallback that recomputed a 15%-off total client-side
    // caused a P0 double-discount vs the PaymentIntent — removed.
    if (fare && fare.first_ride_wins) {
      return Number(Math.max(0, Number(fare.base_cycle || 0) - Number(fare.cycle_total || 0)).toFixed(2));
    }
    return 0;
  }, [fare]);
  // cycleTotal already includes the usage discount; welcome offer surfaces as a
  // separate "−$X on your first cycle" line.
  const finalTotal = cycleTotal;

  // ---------- handlers ----------
  // tierReqRef carries the latest request token so a slow /geocode/distance
  // response can't overwrite state for an already-cleared address. Without
  // this, clearing the destination mid-flight left the polyline stuck on
  // the previous route.
  const tierReqRef = useRef(0);
  const getTier = useCallback(async (silent = false) => {
    if (!pickup || !destination) return;
    const myToken = ++tierReqRef.current;
    setCalculating(true);
    try {
      const { data } = await api.get("/geocode/distance", { params: { origin: pickup, destination } });
      // Bail if the user cleared an address (or kicked off a newer request)
      // while we were waiting for the API.
      if (myToken !== tierReqRef.current) return;
      setDistance(data.miles);
      setDuration(data.duration_minutes || 0);
      setPolyline(data.polyline || "");
      if (data.supported === false || (data.miles && data.miles > maxMiles)) {
        setUnsupported(true);
        setTierLocked(false);
        if (!silent) toast.error("This route is outside our current service range.");
        return;
      }
      setUnsupported(false);
      const grace = 5;
      const bufMin = Math.max(10, Math.ceil(((data.duration_minutes || 20) + grace) / 5) * 5);
      setBuffer(bufMin);
      setTierLocked(true);
    } catch (e) {
      if (myToken !== tierReqRef.current) return;
      if (!silent) toast.error(formatApiError(e.response?.data?.detail) || "Couldn't calculate route");
    } finally {
      // Always clear `calculating` — gating it by the token check meant the
      // morph surface stayed stuck on "Measuring route…" forever whenever a
      // newer request superseded an older one (every keystroke after a
      // settled value bumped the token).
      setCalculating(false);
    }
  }, [pickup, destination, maxMiles]);

  // Bidirectional entry ↔ ready hand-off choreography.
  //   • `null`       — no hand-off in flight; sheetState follows the
  //                     normal derived value.
  //   • `"forward"`  — replaying the addressing → ready transition
  //                     when the rider taps Continue (or swipes up)
  //                     from the entry surface AFTER a route is
  //                     already locked. Skips the tier refetch since
  //                     the polyline is still on the map.
  //   • `"backward"` — replaying the ready → addressing transition
  //                     when the rider taps Back on wizard step 0 (or
  //                     swipes the wizard down). Keeps the polyline
  //                     intact.
  // Passed down to RouteStep and injected into its sheetState memo so
  // both directions pin the sheet to "calc" for HANDOFF_MS. Once the
  // timer fires we flip `entryOverride` and clear `handoffDir` in the
  // same tick — the derived sheetState resolves to its target
  // ("addressing" for backward, "ready" for forward) and the pill +
  // card slide back INTO view carrying the new content.
  const HANDOFF_MS = 420;
  const [handoffDir, setHandoffDir] = useState(null);
  useEffect(() => {
    if (!handoffDir) return undefined;
    const t = setTimeout(() => {
      if (handoffDir === "backward") setEntryOverride(true);
      else if (handoffDir === "forward") setEntryOverride(false);
      setHandoffDir(null);
    }, HANDOFF_MS);
    return () => clearTimeout(t);
  }, [handoffDir]);

  const routeIsLocked = !!pickup && !!destination && tierLocked && !unsupported;

  // "Repeat last week" auto-lock — when the home card's Repeat CTA
  // navigates here with a hydrated preset, we must lock the tier
  // ourselves (no Continue tap) so the sheet transitions to `ready`
  // and the wizard mounts on Review & Pay.
  const repeatAutoTierRef = useRef(false);
  useEffect(() => {
    if (!jumpToPay) return undefined;
    if (repeatAutoTierRef.current) return undefined;
    if (!pickup || !destination) return undefined;
    if (tierLocked) return undefined;
    // Defer past the reset-on-address-change effect (which increments
    // tierReqRef and would otherwise invalidate the in-flight getTier
    // token). A single macrotask is enough.
    const t = setTimeout(() => {
      if (repeatAutoTierRef.current) return;
      repeatAutoTierRef.current = true;
      getTier(true);
    }, 30);
    return () => clearTimeout(t);
  }, [jumpToPay, pickup, destination, tierLocked]);

  const handleExitToEntry = useCallback(() => {
    // Route not yet locked → no polyline to preserve; just flip the
    // override so the sheet stays on addressing. No exit choreography
    // required because we never left addressing in the first place.
    if (!routeIsLocked) { setEntryOverride(true); return; }
    // Route IS locked — replay the reverse of the initial forward
    // hand-off. Pill and card slide off, then re-enter as addressing.
    if (handoffDir) return;
    setHandoffDir("backward");
  }, [routeIsLocked, handoffDir]);

  const handleReturnToRoute = useCallback(() => {
    // Swipe-up-from-entry return to the wizard. Route is guaranteed
    // to be locked here (canReturnToRoute gates the gesture). Replay
    // the forward hand-off so the transition matches the visual
    // language of the initial Continue tap.
    if (handoffDir) return;
    setHandoffDir("forward");
  }, [handoffDir]);

  const handleContinueFromEntry = useCallback(() => {
    // Continue tap on the address entry surface. Two cases:
    //   1. A route is already locked and we're just returning from the
    //      wizard (entryOverride true) — replay the forward hand-off
    //      without refetching the tier (polyline is still on the map).
    //   2. First time (no cached route yet, or addresses changed) —
    //      clear entryOverride and kick off the normal getTier flow.
    //      drawPhase will drive the addressing → calc → ready arc.
    if (handoffDir) return;
    if (routeIsLocked && entryOverride) {
      setHandoffDir("forward");
      return;
    }
    setEntryOverride(false);
    getTier(false);
  }, [routeIsLocked, entryOverride, handoffDir, getTier]);

  // Reset tier whenever addresses change so the auto-calc re-runs cleanly.
  // Bumping tierReqRef invalidates any in-flight /geocode/distance call so
  // the polyline never re-appears after a clear.
  useEffect(() => {
    tierReqRef.current++;
    setTierLocked(false);
    setPolyline("");
    setUnsupported(false);
    setDistance(0);
    setDuration(0);
    // Also clear calculating immediately so the morph never freezes on
    // "Measuring route…" between keystrokes.
    setCalculating(false);
    // Address changed → the previous route is stale. Drop the entry
    // override so the sheet naturally re-enters `addressing` for a
    // fresh Continue tap once both addresses are complete.
    setEntryOverride(false);
  }, [pickup, destination]);

  // Auto-calc route 600ms after both addresses settle. We block firing
  // while either autocomplete dropdown is OPEN so the rider can finish
  // typing and pick a suggestion before the cinematic preview starts.
  // Auto-calc effect — only kicks off the cinematic preview when BOTH
  // addresses look like fully-formed real addresses. We require:
  //   • a comma (autocomplete suggestions are always "Street, City, ST ZIP")
  //   • length ≥ 12 (filters partial typing like "123" or "abc street")
  //   • neither autocomplete dropdown is open
  // This eliminates the expand/collapse pill loop the rider used to see
  // while typing mid-address — calc + preview is gated behind real input.
  // Auto-advance was removed 2026-02-22: riders MUST tap Continue in
  // the entry card to hand off to the route-draw sequence. This makes
  // the entry state feel deliberate (typing → tap Continue → route
  // draws → step 2) and mirrors the entry.html spec. `getTier(false)`
  // is called on the Continue button in BookingSheet, wired through
  // ScheduleSetup's `onContinue` prop. In edit-route mode the same
  // Continue button appears when the sheet re-enters the addressing
  // state, so recalculation stays a single-tap action.
  const autoTimer = useRef(null);

  const toggleDay = (d) => setDays((arr) => sortDays(arr.includes(d) ? arr.filter((x) => x !== d) : [...arr, d]));

  const pickFrequency = (opt) => {
    // Tapping a frequency card overwrites the day selection with the canonical
    // weekday spread for that bucket — gives the rider an instant baseline
    // they can fine-tune via the day-chips. The previous "preserve-existing"
    // behaviour made the card feel like a no-op.
    setDays(sortDays(opt.defaults));
  };

  const currentStep = STEPS[step] || STEPS[0];
  const currentId = currentStep.id;

  const canAdvance = () => {
    if (currentId === "route") return tierLocked && !unsupported;
    if (currentId === "plan")  return days.length > 0;
    return true;
  };

  const [flipping, setFlipping] = useState(false);

  const next = () => {
    if (!canAdvance()) {
      if (currentId === "route" && !tierLocked) toast.error("Confirm your route to continue");
      else if (currentId === "plan" && !days.length) toast.error("Pick at least one day");
      return;
    }
    // From the route step → plan step, play a card-flip-away on the route
    // morph surface, then swap. This makes the slide-to-confirm card feel
    // like it physically opens into the larger schedule builder instead of
    // a new page rendering. Subsequent steps (only "back" exists today)
    // keep the simple swap.
    if (currentId === "route") {
      setFlipping(true);
      setTimeout(() => {
        setStep((s) => Math.min(STEPS.length - 1, s + 1));
        setFlipping(false);
      }, 360);
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));
  const goToStep = useCallback((id) => {
    const idx = STEPS.findIndex((s) => s.id === id);
    if (idx < 0) {
      // eslint-disable-next-line no-console
      console.warn(`[ScheduleSetup] goToStep called with unknown id: ${JSON.stringify(id)}. Valid ids: ${STEPS.map(s => s.id).join(", ")}`);
      return;
    }
    setStep(idx);
  }, []);

  // Stripe Elements checkout sheet — opens after we create the Route. If the
  // backend reports no publishable key configured, we fall back to the hosted
  // Checkout redirect silently.
  // Stripe Elements bottom-sheet. We ALWAYS attempt the in-app sheet first —
  // the sheet itself will POST /api/pay/intent to fetch a client_secret
  // and, if that fails or Stripe.js can't load, will invoke `onFallback` so
  // we can transparently redirect to hosted Stripe Checkout.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [pendingRouteId, setPendingRouteId] = useState(null);

  // Helper — when the Elements sheet can't render, gracefully route the
  // rider to hosted Stripe Checkout. Preserves route_id so the same
  // payment_transactions row threads through the webhook + activation flow.
  const fallbackToHostedCheckout = async (routeId, reason = "elements_unavailable") => {
    console.warn(`[checkout] falling back to hosted Stripe Checkout — reason=${reason}`);
    try {
      toast.message(`Redirecting to Stripe… (${reason})`, {
        description: "You'll complete payment on Stripe's secure page.",
      });
      const { data: ck } = await api.post("/checkout/session", {
        route_id: routeId,
        origin_url: window.location.origin,
        billing_cycle: cycle,
      });
      if (ck?.url) {
        window.location.href = ck.url;
        return true;
      }
      throw new Error("Hosted checkout did not return a URL");
    } catch (e) {
      console.error("[checkout] hosted-checkout fallback ALSO failed:", e);
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Checkout temporarily unavailable. Please try again in a moment.");
      return false;
    }
  };

  const submit = async () => {
    if (!user) { setAuthOpen(true); return; }
    // Controlled-access gate: WAITLIST riders can browse + see quotes
    // but can't book. Route them to the status screen instead of
    // hitting /api/routes (which would 403 anyway).
    if (user.access_status && user.access_status !== "APPROVED") {
      nav("/waitlist/status");
      return;
    }
    // C2 · Draft is done — the rider is committing, so clear the
    // auto-saved wizard draft to avoid resurrecting stale state.
    try { localStorage.removeItem("link.wizard.draft"); } catch (_) { /* noop */ }
    // P1 · Payment moved into Step 3 — the wizard now completes the
    // charge in-sheet via <StripeCheckoutSheet chromeless>. This handler
    // stays around as a compat shim for older callers (e.g. an unlikely
    // race that clicks the wizard's confirm from a stale render); it
    // simply defers to the in-sheet payment path by creating the route
    // and letting the sheet mount. It intentionally does NOT navigate
    // to /checkout/plan — that route is now dead code pending prod
    // Apple Pay verification.
    const rid = await createRouteForCheckout();
    if (rid) setPendingRouteId(rid);
  };

  // Route-create used by BookingSheet Step 3 to eagerly mint the route
  // doc as soon as the rider lands on Review & Pay. Idempotent within
  // the current wizard session — subsequent Step 3 remounts reuse the
  // same routeId so we don't create duplicates.
  const routeCreateRef = useRef({ inFlight: null, id: null });
  const createRouteForCheckout = useCallback(async () => {
    if (routeCreateRef.current.id) return routeCreateRef.current.id;
    if (routeCreateRef.current.inFlight) return routeCreateRef.current.inFlight;
    if (!user) { setAuthOpen(true); return null; }
    if (user.access_status && user.access_status !== "APPROVED") {
      nav("/waitlist/status");
      return null;
    }
    routeCreateRef.current.inFlight = (async () => {
      try {
        const { data: route } = await api.post("/routes", {
          pickup_address: pickup,
          destination_address: destination,
          days,
          arrival_time: arrival,
          return_time: ret,
          buffer_minutes: Number(buffer),
          distance_miles: Number(distance),
          billing_cycle: cycle,
          day_overrides: dayOverrides || {},
          seats_needed: Number(seats || 1),
          transport_preference: pref || "shared",
        });
        routeCreateRef.current.id = route.id;
        setPendingRouteId(route.id);
        try { localStorage.removeItem("link.wizard.draft"); } catch (_) { /* noop */ }
        return route.id;
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
        return null;
      } finally {
        routeCreateRef.current.inFlight = null;
      }
    })();
    return routeCreateRef.current.inFlight;
  }, [user, pickup, destination, days, arrival, ret, buffer, distance, cycle, dayOverrides, seats, pref, nav]);

  // Reset the route-create memo whenever the underlying plan changes so
  // an edit → return-to-review path re-creates a route with the new data
  // instead of re-using the stale one.
  useEffect(() => {
    routeCreateRef.current = { inFlight: null, id: null };
    setPendingRouteId(null);
  }, [pickup, destination, JSON.stringify(days), arrival, ret, cycle, seats, pref]);

  // In-sheet payment success — same routing as the retired
  // /checkout/plan success handler.
  const handleInSheetPaymentSuccess = () => {
    routeCreateRef.current = { inFlight: null, id: null };
    setPendingRouteId(null);
    window.location.href = "/trips?just_paid=1";
  };

  const handleCheckoutSuccess = () => {
    setSheetOpen(false);
    setPendingRouteId(null);
    // Route has been activated server-side; send the rider to /trips so the
    // first upcoming trip is right there waiting for them.
    window.location.href = "/trips?just_paid=1";
  };

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center label-xs text-[#515E7B]">Loading…</div>;

  // Static-map fallback URL — only used by RouteMap if the Google Maps JS API
  // fails to authenticate (e.g. Maps JS not enabled in GCP). Keeps the screen
  // useful instead of blank while operators flip the flag.
  const BACKEND = typeof window !== "undefined" ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
  const fallbackMapSrc = pickup && destination
    ? `${BACKEND}/api/maps/preview?origin=${encodeURIComponent(pickup)}&destination=${encodeURIComponent(destination)}${polyline ? `&polyline=${encodeURIComponent(polyline)}` : ""}&size=800x400&scale=2`
    : `${BACKEND}/api/maps/preview?center=${userLoc.lat},${userLoc.lng}&zoom=16&size=800x400&scale=2`;

  return (
    <div
      className="h-[100dvh] flex flex-col overflow-hidden"
      style={{ background: "var(--dusk-bg)" }}
      data-testid="schedule-setup"
      data-dusk="1"
    >
      {/* Cancelled banner only — progress bar removed in favor of the morph
          surface itself signalling state. The Plan step has its own back chip. */}
      {cancelledBanner && (
        <div
          className={`${currentId === "route" || currentId === "plan" ? "fixed left-1/2 -translate-x-1/2 z-20" : ""}`}
          style={currentId === "route" || currentId === "plan"
            ? { top: "calc(env(safe-area-inset-top, 0px) + 64px)", maxWidth: "min(640px, calc(100vw - 24px))", width: "100%" }
            : undefined}
        >
          <div className="mx-4 sm:mx-6 border-l-2 border-[#ed6500] bg-white/95 backdrop-blur p-3 flex items-start justify-between gap-3 shadow-lg rounded-xl" data-testid="checkout-cancelled-banner">
            <div className="min-w-0">
              <div className="label-xs text-[#ed6500]">Payment canceled</div>
              <p className="text-xs text-[#515E7B] mt-0.5">No charge was made — pick up where you left off.</p>
            </div>
            <button
              onClick={() => setCancelledBanner(false)}
              className="text-[#515E7B] hover:text-[#0A1128] p-1 shrink-0"
              aria-label="Dismiss"
              data-testid="checkout-cancelled-dismiss"
            ><X size={16} /></button>
          </div>
        </div>
      )}

      {/* Wizard content — both steps render on top of a fixed full-screen map.
          Step 0 (route): address inputs + bottom morph (idle/calc/ready/slide).
          Step 1 (plan):  unified morphing card with frequency/days/times/billing/slide. */}
      <div className="flex-1 flex flex-col" data-testid="wizard-content">
        {/* B3.5 Task 1: The hardcoded "$10 off your first ride" banner used to
            mount here on the map/home screen. It has been REMOVED because
            (a) it overlapped the TopAvatarPill and (b) it hard-coded a $10
            figure that didn't match the actual coupon logic. The dynamic
            promo banner now lives inside BookingSheet Step 3 (Review) and
            on the CheckoutPlan surface only — the two moments where the
            rider is actually committing to a plan.
            See /pricing/first-ride for the source of truth (discount_pct). */}
        <div key={currentId} className="flex-1 flex flex-col animate-step-in">
          {currentId === "route" && (
            <RouteStep
              pickup={pickup} setPickup={setPickup}
              destination={destination} setDestination={setDestination}
              tierLocked={tierLocked} unsupported={unsupported} calculating={calculating}
              currentTier={currentTier} distance={distance} duration={duration}
              maxMiles={maxMiles}
              dailyRate={dailyRate}
              cycleTotal={cycleTotal}
              firstRideDiscount={firstRideDiscount}
              finalTotal={finalTotal}
              fare={fare}
              polyline={polyline}
              userLoc={userLoc}
              hasUserLoc={hasUserLoc}
              fallbackMapSrc={fallbackMapSrc}
              onRetry={() => getTier(false)}
              onContinue={handleContinueFromEntry}
              onExitToEntry={handleExitToEntry}
              onReturnToRoute={handleReturnToRoute}
              canReturnToRoute={!!pickup && !!destination && tierLocked && !unsupported && entryOverride}
              entryOverride={entryOverride}
              handoffDir={handoffDir}
              // Plan-mode is the only mode for the rider experience now.
              // The legacy multi-step wizard (Arrive By / Depart By /
              // Flexible Schedule) has been retired — the plan card on `/`
              // is the only scheduling surface. /schedule is a redirect to
              // / via the router.
              planMode
              days={days} setDays={setDays}
              pickupWindow={pickupWindow} setPickupWindow={setPickupWindow}
              returnWindow={returnWindow} setReturnWindow={setReturnWindow}
              seats={seats} setSeats={setSeats}
              pref={pref} setPref={setPref}
              cycle={cycle} setCycle={setCycle} setCycleUserPicked={setCycleUserPicked}
              submitting={submitting}
              onConfirm={submit}
              onCreateRoute={createRouteForCheckout}
              onPaymentSuccess={handleInSheetPaymentSuccess}
              user={user}
              flipping={flipping}
              upcomingTrip={upcomingTrip}
              pickupDropdownOpenTop={pickupDropdownOpenTop}
              setPickupDropdownOpenTop={setPickupDropdownOpenTop}
              destDropdownOpenTop={destDropdownOpenTop}
              setDestDropdownOpenTop={setDestDropdownOpenTop}
              firstRide={firstRide}
              initialWizardStepIdx={initialWizardStepIdx}
            />
          )}
          {currentId === "plan" && (
            <PlanStep
              pickup={pickup} destination={destination} distance={distance} duration={duration}
              polyline={polyline}
              userLoc={userLoc} hasUserLoc={hasUserLoc} fallbackMapSrc={fallbackMapSrc}
              currentTier={currentTier}
              days={days} setDays={setDays} toggleDay={toggleDay}
              pickFrequency={pickFrequency}
              arrival={arrival} setArrival={setArrival}
              ret={ret} setRet={setRet}
              pickupTime={pickupTime}
              dayOverrides={dayOverrides} setDayOverrides={setDayOverrides}
              pickupWindow={pickupWindow} returnWindow={returnWindow}
              cycle={cycle} setCycle={setCycle} cycleUserPicked={cycleUserPicked} setCycleUserPicked={setCycleUserPicked}
              currentCycle={currentCycle}
              dailyRate={dailyRate}
              cycleTotal={cycleTotal}
              firstRideDiscount={firstRideDiscount}
              finalTotal={finalTotal}
              welcomeDaily={welcomeDailyRate}
              isWelcome={firstRide.eligible && !!user && firstRideDiscount > 0}
              user={user}
              submitting={submitting}
              onBack={back}
              onSubmit={submit}
            />
          )}
        </div>
      </div>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => submit()} />

      {/* In-app Stripe Elements checkout sheet. Opened by the submit flow
          when a publishable key is configured (fallback redirects to hosted
          Checkout). amountDisplay + subtitle summarise what the rider is
          about to pay before we show the PaymentElement. */}
      <StripeCheckoutSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onSuccess={handleCheckoutSuccess}
        onFallback={(reason) => {
          // Sheet detected an Elements-side failure (intent fetch / Stripe.js
          // load / Element mount). Close it and seamlessly redirect to hosted
          // Stripe Checkout — the rider still completes payment, just on
          // stripe.com instead of in-app.
          const rid = pendingRouteId;
          setSheetOpen(false);
          setPendingRouteId(null);
          if (rid) fallbackToHostedCheckout(rid, reason);
        }}
        routeId={pendingRouteId}
        billingCycle={cycle}
        amountDisplay={
          cycle === "payg"
            ? `$${(dailyRate * days.length || 0).toFixed(2)}`
            : `$${finalTotal.toFixed(2)}`
        }
        subtitle={
          currentTier && days.length > 0
            ? `${cycle === "payg" ? "One-time" : "Weekly"} · ${currentTier.name} · ${days.length} ${days.length === 1 ? "day" : "days"}`
            : undefined
        }
      />

      {/* Wizard transition keyframes.
          step-in       → "card bloom": new step opens forward + grows from
                          the bottom edge, like the slide-to-confirm card
                          turning over and expanding into the schedule builder.
          card-flipaway → played on the route morph surface immediately after
                          slide-to-confirm fires; the card tilts back and
                          fades, then the next step blooms in. The two pieces
                          combine into one continuous "card opens" gesture. */}
      <style>{`
        @keyframes step-in {
          0%   { opacity: 0; transform: perspective(1400px) rotateX(38deg) scale(0.84) translateY(48px); transform-origin: 50% 100%; }
          55%  { opacity: 1; }
          100% { opacity: 1; transform: perspective(1400px) rotateX(0deg) scale(1) translateY(0); transform-origin: 50% 100%; }
        }
        .animate-step-in { animation: step-in 540ms cubic-bezier(0.22, 1, 0.36, 1); transform-style: preserve-3d; }

        @keyframes card-flipaway {
          0%   { opacity: 1; transform: perspective(1400px) rotateX(0deg) scale(1); transform-origin: 50% 100%; }
          100% { opacity: 0; transform: perspective(1400px) rotateX(-58deg) scale(0.94) translateY(-18px); transform-origin: 50% 100%; }
        }
        .animate-card-flipaway { animation: card-flipaway 320ms cubic-bezier(0.4, 0, 0.7, 0.4) forwards; }

        @keyframes route-card-up { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .animate-route-card { animation: route-card-up 320ms cubic-bezier(0.16, 1, 0.3, 1); }
        @keyframes pill-morph {
          0%   { opacity: 0; transform: translateY(8px) scale(0.96); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-pill-morph { animation: pill-morph 260ms cubic-bezier(0.34, 1.56, 0.64, 1); }
        /* iOS-style wheel: hide native scrollbar across browsers */
        .wheel-scroll::-webkit-scrollbar { display: none; }
        .wheel-scroll { -ms-overflow-style: none; scrollbar-width: none; -webkit-overflow-scrolling: touch; }
      `}</style>
    </div>
  );
}

/* =====================  STEP 0 — ROUTE CONFIRMATION  ===================== */
/**
 * Map-first booking screen, à la Uber/Lyft.
 * - Map is the screen background, ~58vh tall, edge-to-edge full-bleed.
 * - Address inputs float in a glass card overlaid at the top of the map.
 * - On mount the map renders immediately at the user's geolocation (KC fallback).
 *   When pickup + destination both resolve, the static-map endpoint switches to
 *   a polyline route preview with auto-fit bounds.
 * - A floating distance/duration card lifts in from the bottom of the map once
 *   the route is calculated; the tier reveal card sits below the map.
 */
function RouteStep({ pickup, setPickup, destination, setDestination, tierLocked, unsupported, calculating, currentTier, distance, duration, maxMiles, dailyRate, cycleTotal, firstRideDiscount, finalTotal, fare, polyline, userLoc, hasUserLoc, fallbackMapSrc, onRetry, onContinue, continueCtaLabel, continueCtaTestId, initialWizardStepIdx, onExitToEntry, onReturnToRoute, canReturnToRoute, entryOverride, handoffDir, onConfirm, onCreateRoute, onPaymentSuccess, user, flipping, upcomingTrip,
  // Plan-mode props — when present, BookingSheet renders the full
  // transportation plan inline (route-card-as-plan) and the slide-to-
  // schedule action submits the route directly.
  planMode, days, setDays, pickupWindow, setPickupWindow, returnWindow, setReturnWindow, seats, setSeats, pref, setPref,
  cycle, setCycle, setCycleUserPicked,
  submitting,
  // Address-autocomplete dropdown state lifted to ScheduleSetup so the
  // route-quote auto-calc can gate on it.
  pickupDropdownOpenTop, setPickupDropdownOpenTop,
  destDropdownOpenTop, setDestDropdownOpenTop,
  // B3.5 Task 1 — dynamic first-ride discount (percent or fixed) passed
  // through to BookingSheet's Step 3 review so the banner reflects the
  // ACTUAL coupon logic, not a hardcoded $10.
  firstRide,
}) {
  // B3 · Reduce-motion detector for RouteStep — gates cinematicDraw so
  // riders with `prefers-reduced-motion: reduce` see the polyline pop
  // in statically instead of tracing (~1.1s). Duplicated at this scope
  // because RouteStep is a separately-defined function component.
  const reduceMotion = useReducedMotion();
  // Sister helper to the parent's looksLikeAddress — duplicated here
  // because RouteStep is a separately-defined function component.
  const looksLikeAddress = (s) => {
    if (!s) return false;
    const t = s.trim();
    return t.includes(",") && t.length >= 12;
  };
  // Distance-based daily price preview — surfaces in the ready-sheet as
  // soon as the route is locked, so the rider sees what they'll pay before
  // committing to the schedule builder. Value comes from the parent's
  // server-driven quote (the matrix never crosses the wire).
  const previewRate = Number(dailyRate || 0);
  // Mirror lifted state for internal use (sheetState memo).
  const pickupDropdownOpen = pickupDropdownOpenTop;
  const destDropdownOpen = destDropdownOpenTop;
  const setPickupDropdownOpen = setPickupDropdownOpenTop;
  const setDestDropdownOpen = setDestDropdownOpenTop;

  // User-explicit collapse: set true when the user drags the sheet down or
  // taps the map. Cleared when they tap the sheet / pickup field again, or
  // when the route reaches a new ready state.
  const [collapsedByUser, setCollapsedByUser] = useState(false);
  // User-explicit expansion: set true when the user taps the collapsed
  // pill. Forces sheetState to 'addressing' even when no derived signal
  // (pickup/dest/dropdown) is truthy yet — without this the empty pill
  // would stay collapsed forever.
  const [expandedByUser, setExpandedByUser] = useState(false);
  // Flip to true once pickup has been explicitly picked; gate the auto-focus
  // on destination so we only fire it on the actual fill event (not on
  // remount or when hydrating from localStorage).  Guarded against
  // manual typing by requiring a substantial value length — a selected
  // autocomplete suggestion is always a full address ("Street, City,
  // State ZIP"), which comfortably exceeds the threshold.
  const [focusDest, setFocusDest] = useState(false);
  const prevPickupRef = useRef(pickup);
  useEffect(() => {
    const prev = prevPickupRef.current;
    const isSubstantial = (pickup || "").trim().length >= 10;
    const hadSomething = (prev || "").trim().length >= 3;
    // Fire only when pickup transitions to a substantial value AND it
    // wasn't already a substantial value (avoid re-firing on edits).
    if (isSubstantial && !hadSomething && !destination) {
      setFocusDest(true);
    }
    prevPickupRef.current = pickup;
  }, [pickup, destination]);
  // Consume the focus flag after it fires once so editing destination later
  // doesn't re-steal focus.
  useEffect(() => {
    if (focusDest) {
      const t = setTimeout(() => setFocusDest(false), 400);
      return () => clearTimeout(t);
    }
  }, [focusDest]);

  // Route is "ready" — derived from the actual address values + the backend
  // tier-locked flag. Feeds the sheet state machine.
  const routeReady = !!pickup && !!destination && tierLocked && !unsupported;

  // Cinematic hand-off — Sequence 1 → Sequence 2 is driven by
  // ANIMATION-END events fired by RouteMap, not independent timers.
  //   drawPhase:
  //     'idle'    — no calc in flight
  //     'drawing' — polyline actively being traced across the map
  //                 (sheet is off-screen, no "tracing" label, animation is
  //                 the feedback)
  //     'chip'    — draw completed, result chip is up and holding ~1s
  //                 so the rider reads the numbers
  //     'settled' — chip has held long enough; sheet + frost engage next
  //                 tick as one motion
  //
  // Timing anchors:
  //   • RouteMap.onDrawComplete → advances drawing → chip
  //   • CHIP_HOLD_MS setTimeout → advances chip → settled
  // The chip preview pill was removed per user request — the polyline
  // draw itself is the entire visual event. Draw completes → jump
  // straight to `settled` so the sheet rises immediately with the
  // polyline still visible on the map.
  const CHIP_HOLD_MS = 0;
  const [drawPhase, setDrawPhase] = useState("idle");
  useEffect(() => {
    if (!tierLocked) { setDrawPhase("idle"); return; }
    setDrawPhase("drawing");
  }, [tierLocked, polyline]);
  useEffect(() => {
    if (drawPhase !== "drawing") return undefined;
    if (reduceMotion) {
      setDrawPhase("settled");
      return undefined;
    }
    const safety = setTimeout(() => setDrawPhase((p) => p === "drawing" ? "settled" : p), 2600);
    return () => clearTimeout(safety);
  }, [drawPhase, reduceMotion]);
  const handleDrawComplete = useCallback(() => {
    setDrawPhase((p) => (p === "drawing" ? "settled" : p));
  }, []);

  // Sheet state machine — derived single source of truth.
  // collapsed  → only pickup field visible (or trip-active pill)
  // addressing → pickup + destination fields
  // calc       → sheet exited, polyline being drawn on a clean map
  // chip       → result chip is up (sheet still exited), holding ~1s
  // ready      → route summary + slide-to-confirm
  //
  // CRITICAL: we only enter `calc` once BOTH addresses look complete
  // (have a comma + length ≥ 12 — i.e. accepted from autocomplete OR
  // typed in full). Without this gate the pill loops between
  // expanding ("addressing") and collapsing ("calc") on every keystroke
  // while the user is still mid-typing an address.
  const sheetState = useMemo(() => {
    // Address entry is ALWAYS expanded from first paint. The collapsed
    // (76px pill) state was removed per product direction — the card
    // stays in `addressing` even before the rider types anything so the
    // Where-to? title, pickup + destination fields, and Continue button
    // are always visible.
    if (unsupported) return "addressing";
    // Bidirectional hand-off in flight — pin the sheet to `calc` so
    // both the address card and the account pill exit off-screen.
    // Runs for HANDOFF_MS, then resolves to addressing (backward) or
    // ready (forward) on the same tick handoffDir clears.
    if (handoffDir) return "calc";
    // Entry override — rider swiped down from the wizard (or tapped
    // Back on Step 0). Keep the polyline drawn on the map but slide
    // the sheet back to the address entry surface. `routeReady` stays
    // true underneath so a subsequent swipe-up returns instantly to
    // the exact wizard step they left.
    if (entryOverride && routeReady) return "addressing";
    // Sheet lands ready only once the draw sequence has settled.
    if (routeReady && drawPhase === "settled") return "ready";
    // While the tier is locked but the polyline draw hasn't finished
    // yet (drawPhase `idle` briefly before the effect flips it to
    // `drawing`, or during the RAF draw itself), keep the sheet in
    // `calc` — this suppresses the one-render flash back to
    // `addressing` that made the sheet appear to rise the instant
    // Continue was tapped, before the polyline had drawn.
    if (routeReady) return "calc";
    const bothReal = looksLikeAddress(pickup) && looksLikeAddress(destination);
    // Fetching the route (getTier in flight) — sheet exits during
    // this window so the map is unobstructed while we wait for the
    // polyline to come back.
    if (bothReal && calculating) return "calc";
    return "addressing";
  }, [unsupported, handoffDir, entryOverride, routeReady, drawPhase, calculating, pickup, destination]);

  // Whenever the route transitions into a new material state (calc→ready,
  // addressing→calc, etc.), unstick the user-collapsed flag so the sheet
  // reveals the new info. The flag only sticks within the SAME logical
  // state — e.g. a user dismisses the "addressing" sheet and it stays
  // collapsed until they tap to re-open.
  useEffect(() => {
    setCollapsedByUser(false);
  }, [routeReady, calculating, unsupported]);

  const handleMapTap = useCallback(() => {
    // Only meaningful in expanded states; in collapsed we stay collapsed.
    // Explicitly excludes `calc` + `chip` — those phases the sheet has
    // already exited the frame, so a map tap is just the rider watching
    // the polyline draw, not a dismiss gesture.
    if (sheetState === "addressing" || sheetState === "ready") {
      setCollapsedByUser(true);
      setExpandedByUser(false);
    }
  }, [sheetState]);

  // When the user taps the collapsed pill to expand, surface a one-shot
  // focus flag for the pickup input so the keyboard pops immediately and
  // they can start typing without a second tap.  The flag is consumed
  // inside BookingSheet via AddressInput.autoFocus and reset shortly
  // after.
  const [focusPickup, setFocusPickup] = useState(false);
  useEffect(() => {
    if (focusPickup) {
      const t = setTimeout(() => setFocusPickup(false), 400);
      return () => clearTimeout(t);
    }
  }, [focusPickup]);
  const handleExpand = useCallback(() => {
    setCollapsedByUser(false);
    setExpandedByUser(true);
    if (!pickup) setFocusPickup(true);
  }, [pickup]);
  const handleCollapse = useCallback(() => {
    setCollapsedByUser(true);
    setExpandedByUser(false);
  }, []);

  // Publish sheetState to <html> so the globally-mounted TopAvatarPill
  // (rendered by RiderApp shell, not this page) can respond to the
  // Continue→scheduler choreography. On `calc` the pill slides UP and
  // fades out on the SAME beat as the address card sliding DOWN;
  // returns on `ready`. Matching CSS lives in
  // /app/frontend/src/index.css under `[data-rider-sheet-state]`.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.riderSheetState = sheetState;
    return () => { delete root.dataset.riderSheetState; };
  }, [sheetState]);

  return (
    <section data-testid="page-route" className="flex-1 relative">
      {/* Full-fixed map container — covers the entire viewport behind the
          sheet. Tapping the map collapses the sheet (except when already
          collapsed). */}
      <div
        className="map-container fixed inset-0"
        style={{ height: "100dvh", minHeight: "-webkit-fill-available", zIndex: 0 }}
        data-testid="route-map"
      >
        <RouteMap
          center={userLoc}
          polyline={(tierLocked && pickup && destination) ? polyline : ""}
          userLocation={hasUserLoc ? userLoc : null}
          fallbackSrc={fallbackMapSrc}
          onMapClick={handleMapTap}
          cinematicDraw={!reduceMotion && (sheetState === "calc" || sheetState === "chip" || sheetState === "ready")}
          onDrawComplete={handleDrawComplete}
          recenterAnchor="top"
        />
      </div>

      {/* Frosted map treatment — Round 4 · Sequence 2 hand-off.
          Frost is CLEAR during idle, addressing, calc, and chip so the
          rider can see the map, the address they're typing against, and
          the polyline drawing across the frame. Frost engages ONLY on
          `ready` — at the SAME instant the booking sheet begins its
          rise from below, with the SAME duration + easing curve (400ms
          / ease-out) so both animations land on the same beat.
          Reduced-motion collapses to an instant swap. */}
      <div
        className="fixed inset-0 pointer-events-none link-map-frost"
        style={{
          zIndex: 3,
          opacity: (sheetState === "ready" || sheetState === "addressing") ? 1 : 0,
          backdropFilter: sheetState === "ready" ? "blur(10px)" : "blur(0px)",
          WebkitBackdropFilter: sheetState === "ready" ? "blur(10px)" : "blur(0px)",
          background: sheetState === "ready"
            ? "linear-gradient(180deg, rgba(10,17,40,0.45) 0%, rgba(10,17,40,0.6) 30%, rgba(7,13,31,0.9) 100%)"
            // Addressing state — soft dimming ONLY behind/around the
            // address card (bottom ~40% of the viewport). Fully
            // transparent above the card so the map reads clear above
            // it. This pulls eye focus to the card without shading the
            // rest of the frame.
            : sheetState === "addressing"
              ? "linear-gradient(180deg, rgba(10,17,40,0) 0%, rgba(10,17,40,0) 58%, rgba(10,17,40,0.28) 75%, rgba(7,13,31,0.55) 100%)"
              : "linear-gradient(180deg, rgba(10,17,40,0) 0%, rgba(10,17,40,0) 60%, rgba(7,13,31,0) 100%)",
          transition: reduceMotion
            ? "opacity 0ms, backdrop-filter 0ms, background 0ms"
            : "opacity 400ms cubic-bezier(0.16, 1, 0.3, 1), backdrop-filter 400ms cubic-bezier(0.16, 1, 0.3, 1), background 400ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        data-testid="map-frost-overlay"
        data-frost-level={sheetState}
        aria-hidden="true"
      />

      {/* Top legibility gradient removed on /book — the cream fade
          created a "gradient stripe → hard cut" against the map on
          mobile. The dusk sheet handles its own text legibility. */}

      {/* Pill halo removed on /book — TopAvatarPill is killed globally
          per the account-morph rewire, so there's nothing floating at
          the top that needs a backdrop. Any gradient here would create
          a "hard cut" against the map on mobile. */}

      {/* MapGreeting no-ops after Feb 2026 rewire — retired. */}

      {/* Out-of-range warning floats above the sheet so the rider can
          dismiss + retry without the sheet obscuring it. */}
      {unsupported && (
        <div
          className="fixed inset-x-0 px-3 sm:px-4 pointer-events-none animate-route-card"
          style={{
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 56dvh + 16px)",
            zIndex: 35,
          }}
        >
          <div className="max-w-2xl mx-auto pointer-events-auto">
            <div className="bg-white border-l-2 border-[#ed6500] p-4 shadow-lg rounded-2xl" data-testid="route-unsupported">
              <div className="label-xs text-[#ed6500]">Out of range</div>
              <p className="text-sm text-[#0A1128] mt-1">
                Routes up to <span className="font-semibold">{maxMiles} mi</span> one-way are supported. Try a shorter route or contact us for special arrangements.
              </p>
              <button
                onClick={onRetry}
                className="mt-3 text-[10px] tracking-widest uppercase font-semibold text-[#ed6500] hover:underline"
                data-testid="route-retry"
              >Re-measure</button>
            </div>
          </div>
        </div>
      )}

      <BookingSheet
        state={sheetState}
        pickup={pickup}
        setPickup={setPickup}
        destination={destination}
        setDestination={setDestination}
        onPickupOpenChange={setPickupDropdownOpen}
        onDestOpenChange={setDestDropdownOpen}
        focusPickup={focusPickup}
        focusDest={focusDest}
        onExpand={handleExpand}
        onCollapse={handleCollapse}
        onContinue={onContinue}
        continueCtaLabel={continueCtaLabel}
        continueCtaTestId={continueCtaTestId}
        onExitToEntry={onExitToEntry}
        onReturnToRoute={onReturnToRoute}
        canReturnToRoute={canReturnToRoute}
        riderFirstName={
          user?.firstName
          || user?.first_name
          || ((user?.name || "").trim().split(/\s+/)[0] || null)
          || null
        }
        currentTier={currentTier}
        distance={distance}
        duration={duration}
        previewRate={previewRate}
        cycleTotal={cycleTotal}
        firstRideDiscount={firstRideDiscount}
        finalTotal={finalTotal}
        fare={fare}
        dailyRate={dailyRate}
        onConfirm={onConfirm}
        onCreateRoute={onCreateRoute}
        onPaymentSuccess={onPaymentSuccess}
        flipping={flipping}
        upcomingTrip={upcomingTrip}
        days={planMode ? days : undefined}
        setDays={planMode ? setDays : undefined}
        pickupWindow={planMode ? pickupWindow : undefined}
        setPickupWindow={planMode ? setPickupWindow : undefined}
        returnWindow={planMode ? returnWindow : undefined}
        setReturnWindow={planMode ? setReturnWindow : undefined}
        seats={planMode ? seats : undefined}
        setSeats={planMode ? setSeats : undefined}
        pref={planMode ? pref : undefined}
        setPref={planMode ? setPref : undefined}
        cycle={planMode ? cycle : undefined}
        setCycle={planMode ? setCycle : undefined}
        setCycleUserPicked={planMode ? setCycleUserPicked : undefined}
        submitting={planMode ? submitting : undefined}
        firstRide={firstRide}
        initialWizardStepIdx={initialWizardStepIdx}
      />
    </section>
  );
}

/* =====================  STEP 2 — TIME WINDOW  ===================== */
function TimesStep({ arrival, setArrival, ret, setRet, pickupTime, days, buffer }) {
  // Scheduling engine pre-flight:  the backend validates the rider's
  // picked times against driver commitments + admin blocks over the
  // next 4 weeks.  When it returns a suggestion we apply it silently —
  // the UX promise is "always present a solution", never an error.
  const [check, setCheck] = useState(null); // { ok, conflicts, suggestion } | null
  const [adjusted, setAdjusted] = useState(null); // { arrival_time, return_time } after an auto-nudge
  const checkTimer = useRef(null);
  // Track the LAST arrival/ret we sent to the backend so that, if the
  // user edits them again, we know to clear the "adjusted" hint.
  const lastCheckedRef = useRef({ arrival: null, ret: null });

  useEffect(() => {
    if (!arrival || !ret || days.length === 0) { setCheck(null); return; }
    // User manually edited after an adjustment — clear the nudge hint.
    if (adjusted && (arrival !== adjusted.arrival_time || ret !== adjusted.return_time)) {
      setAdjusted(null);
    }
    if (checkTimer.current) clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/schedule/availability-check", {
          days, arrival_time: arrival, return_time: ret,
          buffer_minutes: Number(buffer || 30), horizon_weeks: 4,
        });
        setCheck(data);
        lastCheckedRef.current = { arrival, ret };
        // Intelligent auto-adjust: if the server returned a working
        // suggestion, silently apply it and surface a calm confirmation.
        if (data && !data.ok && data.suggestion && (
          data.suggestion.arrival_time !== arrival ||
          data.suggestion.return_time !== ret
        )) {
          setArrival(data.suggestion.arrival_time);
          setRet(data.suggestion.return_time);
          setAdjusted({
            arrival_time: data.suggestion.arrival_time,
            return_time: data.suggestion.return_time,
          });
        }
      } catch (_) {
        // Quiet failure — pre-flight only, never blocks the booking flow.
        setCheck(null);
      }
    }, 500);
    return () => { if (checkTimer.current) clearTimeout(checkTimer.current); };
  }, [days, arrival, ret, buffer, adjusted, setArrival, setRet]);

  return (
    <section data-testid="page-times" className="flex-1 px-4 sm:px-6 pb-3 overflow-y-auto">
      <header className="mb-5">
        <h1 className="font-serif font-light text-3xl sm:text-4xl leading-tight text-[#0A1128]">
          Pickup &amp; return windows
        </h1>
        <p className="text-xs sm:text-sm text-[#515E7B] mt-1">Spin the wheels to dial in your typical schedule. We&rsquo;ll set a 15-minute pickup window automatically.</p>
      </header>

      <div className="space-y-5 mb-3">
        {/* Arrival tri-wheel */}
        <div data-testid="times-arrival">
          <div className="flex items-center gap-2 mb-2">
            <Sun size={14} className="text-[#ed6500]" />
            <h2 className="label-xs text-[#0A1128]">Arrive by</h2>
          </div>
          <TimeTriPicker value={arrival} onChange={setArrival} testId="arrival-wheel" />
        </div>

        {/* Return tri-wheel */}
        <div data-testid="times-return">
          <div className="flex items-center gap-2 mb-2">
            <Moon size={14} className="text-[#5c97f7]" />
            <h2 className="label-xs text-[#0A1128]">Depart by</h2>
          </div>
          <TimeTriPicker value={ret} onChange={setRet} testId="return-wheel" />
        </div>
      </div>

      {pickupTime && (
        <div className="mt-4 border-l-2 border-[#5c97f7] bg-white p-3 text-xs text-[#515E7B]" data-testid="times-pickup-hint">
          Your pickup window will be <span className="text-[#0A1128] font-semibold" data-testid="times-pickup-value">{formatPickupWindow(pickupTime)}</span>.
          {" "}You&rsquo;ll arrive by <span className="text-[#0A1128] font-semibold">{formatTime12(arrival)}</span> and head home at <span className="text-[#0A1128] font-semibold">{formatTime12(ret)}</span>.
        </div>
      )}

      {/* Calm auto-adjust confirmation — replaces the old error-toned
          conflict card.  The system ALWAYS presents a solution. */}
      {adjusted && (
        <div
          className="mt-4 border-l-2 border-[#5c97f7] bg-white p-3"
          data-testid="times-adjusted"
        >
          <div className="label-xs text-[#5c97f7]">Adjusted pickup</div>
          <div className="font-serif text-base text-[#0A1128] mt-0.5" data-testid="times-adjusted-value">
            Arrive by {formatTime12(adjusted.arrival_time)} · Depart by {formatTime12(adjusted.return_time)}
          </div>
          <p className="text-[11px] text-[#515E7B] mt-1">Ensures on-time arrival.</p>
        </div>
      )}

      {days.length > 0 && (
        <div className="mt-3 text-[11px] text-[#515E7B]">
          Applies to all {days.length} selected day{days.length === 1 ? "" : "s"}: {days.join(" · ")}.
        </div>
      )}
    </section>
  );
}

/* Tri-wheel: Hour · Minute · AM/PM. Combines three TimeWheel primitives into a
   single picker that emits 24-hour "HH:MM" strings — keeps the rest of the
   wizard's state shape unchanged. */
function TimeTriPicker({ value, onChange, testId }) {
  const parts = useMemo(() => from24h(value), [value]);
  const setHour = useCallback((id) => onChange(to24h(Number(id), parts.minute, parts.ampm)), [onChange, parts.minute, parts.ampm]);
  const setMinute = useCallback((id) => onChange(to24h(parts.hour12, id, parts.ampm)), [onChange, parts.hour12, parts.ampm]);
  const setAmPm = useCallback((id) => onChange(to24h(parts.hour12, parts.minute, id)), [onChange, parts.hour12, parts.minute]);
  return (
    <div className="grid grid-cols-3 gap-1.5" data-testid={testId}>
      <TimeWheel options={HOUR_OPTIONS}   value={String(parts.hour12)} onChange={setHour}   testId={`${testId}-hour`} />
      <TimeWheel options={MINUTE_OPTIONS} value={parts.minute}         onChange={setMinute} testId={`${testId}-minute`} />
      <TimeWheel options={AMPM_OPTIONS}   value={parts.ampm}           onChange={setAmPm}   testId={`${testId}-ampm`} />
    </div>
  );
}

/* iOS-style snap scroll wheel for time selection */
function TimeWheel({ options, value, onChange, testId }) {
  const ref = useRef(null);
  const ITEM_H = 36; // px — sleeker row height (was 44)
  const VISIBLE = 3; // 1 above + center + 1 below (was 5) — keeps the picker compact
  const HEIGHT = ITEM_H * VISIBLE;
  const PAD = Math.floor(VISIBLE / 2); // rows of spacer above and below
  const scrollTimer = useRef(null);

  // Scroll the wheel to the current value whenever it changes externally.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const idx = options.findIndex((o) => o.id === value);
    if (idx < 0) return;
    const target = idx * ITEM_H;
    if (Math.abs(el.scrollTop - target) > 2) {
      el.scrollTop = target;
    }
  }, [value, options]);

  const onScroll = useCallback(() => {
    if (scrollTimer.current) clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const idx = Math.max(0, Math.min(options.length - 1, Math.round(el.scrollTop / ITEM_H)));
      const next = options[idx];
      if (next && next.id !== value) onChange(next.id);
    }, 80);
  }, [options, value, onChange]);

  const tapRow = (i) => {
    ref.current?.scrollTo({ top: i * ITEM_H, behavior: "smooth" });
    const next = options[i];
    if (next && next.id !== value) onChange(next.id);
  };

  return (
    <div
      className="relative bg-white border hairline overflow-hidden"
      style={{ height: HEIGHT }}
      data-testid={testId}
    >
      {/* Center selection band */}
      <div
        aria-hidden="true"
        className="absolute left-0 right-0 pointer-events-none border-y hairline"
        style={{ top: ITEM_H * PAD, height: ITEM_H, background: "rgba(237,101,0,0.04)" }}
      />
      {/* Top + bottom fade for the wheel illusion */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 pointer-events-none"
        style={{ height: ITEM_H * PAD, background: "linear-gradient(to bottom, #FFFFFF, rgba(255,255,255,0))" }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 pointer-events-none"
        style={{ height: ITEM_H * PAD, background: "linear-gradient(to top, #FFFFFF, rgba(255,255,255,0))" }}
      />
      <div
        ref={ref}
        onScroll={onScroll}
        className="h-full overflow-y-scroll wheel-scroll"
        style={{ scrollSnapType: "y mandatory" }}
      >
        <div style={{ height: ITEM_H * PAD }} aria-hidden="true" />
        {options.map((opt, i) => {
          const active = opt.id === value;
          return (
            <div
              key={opt.id}
              onClick={() => tapRow(i)}
              role="button"
              tabIndex={0}
              className={`flex items-center justify-center select-none cursor-pointer transition-all font-serif ${
                active ? "text-[#0A1128] text-lg sm:text-xl" : "text-[#515E7B]/55 text-sm"
              }`}
              style={{ height: ITEM_H, scrollSnapAlign: "center", scrollSnapStop: "always" }}
              data-testid={`${testId}-${opt.id}`}
            >
              {opt.label}
            </div>
          );
        })}
        <div style={{ height: ITEM_H * PAD }} aria-hidden="true" />
      </div>
    </div>
  );
}


/* =====================  STICKY BOTTOM CTA  ===================== */
function StickyCta({ currentId, isLastStep, canAdvance, tierLocked, currentTier, days, finalTotal, firstRideDiscount, cycleTotal, currentCycle, submitting, user, canBack, onBack, onNext, onSubmit }) {
  const showTotal = (currentId === "schedule" || currentId === "times") && tierLocked && days.length > 0;
  const ctaLabel = (() => {
    if (currentId === "route") return "Confirm Route";
    if (isLastStep) {
      if (submitting) return "Preparing payment…";
      return user ? `Continue to Payment · $${finalTotal.toFixed(2)}` : `Sign in to continue`;
    }
    return "Continue";
  })();
  return (
    <div
      className="sticky bottom-0 left-0 right-0 z-30 bg-[#F9F7F4]/95 backdrop-blur-sm border-t hairline"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="wizard-sticky-cta"
    >
      <div className="px-4 sm:px-6 py-3 flex items-center gap-3">
        <button
          onClick={onBack}
          disabled={!canBack}
          className="w-11 h-11 inline-flex items-center justify-center border hairline bg-white text-[#0A1128] disabled:opacity-30 disabled:cursor-not-allowed shrink-0 rounded-full"
          aria-label="Back"
          data-testid="wizard-back-btn"
        >
          <ArrowLeft size={16} />
        </button>

        {showTotal && (
          <div className="flex-1 min-w-0 text-center" data-testid="wizard-running-total">
            <div className="label-xs text-[#515E7B]">{currentCycle?.one_time ? "One-time" : "Per cycle"}</div>
            <div className="font-serif text-base text-[#0A1128] leading-none mt-0.5">
              ${(cycleTotal - firstRideDiscount).toFixed(0)}
              {firstRideDiscount > 0 && (
                <span className="ml-1.5 text-[10px] tracking-widest uppercase text-[#ed6500] align-middle">−${firstRideDiscount.toFixed(0)}</span>
              )}
            </div>
          </div>
        )}

        <button
          onClick={isLastStep ? onSubmit : onNext}
          disabled={!canAdvance || submitting}
          className={`flex-1 h-11 px-5 inline-flex items-center justify-center gap-2 text-[11px] tracking-widest uppercase font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed rounded-full ${
            canAdvance && !submitting
              ? "bg-[#ed6500] text-white hover:bg-[#d65a00]"
              : "bg-[#E5E1D8] text-[#515E7B]"
          }`}
          data-testid={isLastStep ? "activate-and-pay" : "wizard-next-btn"}
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
          <span className="truncate">{ctaLabel}</span>
          {!submitting && !isLastStep && <ArrowRight size={14} />}
        </button>
      </div>
    </div>
  );
}


/* ===========================================================================
   PlanStep — UNIFIED MORPHING SCHEDULE + BILLING SURFACE
   ===========================================================================
   Replaces the legacy three steps (schedule / times / review) with a single
   tall morph card that sits above the bottom nav and lives on top of the map.
   Inside the same surface the rider:
     · picks a frequency
     · selects days
     · adjusts arrival / return times (inline tap → wheel)
     · toggles billing mode (Weekly ⇄ One-time)
     · sees the live price morph
     · slides to confirm → Stripe Checkout
   No new screens. Everything evolves inside one container. */
function PlanStep({
  pickup, destination, distance, duration, polyline,
  userLoc, hasUserLoc, fallbackMapSrc,
  currentTier,
  days, setDays, toggleDay, pickFrequency,
  arrival, setArrival, ret, setRet, pickupTime,
  dayOverrides, setDayOverrides,
  pickupWindow, returnWindow,
  cycle, setCycle, cycleUserPicked, setCycleUserPicked, currentCycle,
  dailyRate, cycleTotal, firstRideDiscount, finalTotal,
  welcomeDaily, isWelcome,
  user, submitting, onBack, onSubmit,
}) {
  // Smart preselect: if the user lands here without any day chosen, pick today.
  const didPreselect = useRef(false);
  useEffect(() => {
    if (didPreselect.current) return;
    didPreselect.current = true;
    if (days.length === 0) setDays([localWeekdayKey()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Billing cycle selection:
  //   · Smart DEFAULT — 1 day → one-time, 2+ days → weekly subscription.
  //   · Once the user has manually picked a cycle in the review screen we
  //     RESPECT that choice forever after, even if they edit their day
  //     count.  `cycleUserPicked` comes from the parent so step
  //     transitions (PlanStep → ReviewStep → back) don't reset it.
  useEffect(() => {
    if (cycleUserPicked) return;
    if (days.length <= 1 && cycle !== "payg") setCycle("payg");
    if (days.length >= 2 && cycle === "payg") setCycle("weekly");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days.length, cycleUserPicked]);
  const billing = cycle === "payg" ? "onetime" : cycle;

  // Pricing totals — daily × days = one-time, weekly subscription includes
  // the usage discount already folded in via finalTotal.  No formula
  // breakdown surfaces in the new card; just the two headline numbers.
  const oneTimeTotal = useMemo(() => {
    if (!currentTier || days.length === 0) return 0;
    return Number((dailyRate * days.length).toFixed(2));
  }, [currentTier, days.length, dailyRate]);
  const weeklyTotal = useMemo(() => Math.max(0, finalTotal), [finalTotal]);

  // Inline time-wheel popover state.
  const [openTime, setOpenTime] = useState(null); // 'arrival' | 'return' | null
  // Has the rider interacted with the time dials yet?  Flips true on
  // first tap of a TimeChip OR any programmatic arrival/return change.
  // Drives the pickup-hint copy (earliest vs. adjusted).
  const [timesTouched, setTimesTouched] = useState(false);

  // Fetch "earliest pickup" for the untouched-state hint.  Runs once on
  // mount + whenever the duration changes, which is rare post-entry.
  const [earliestLabel, setEarliestLabel] = useState(null);
  useEffect(() => {
    let stop = false;
    if (!duration) { setEarliestLabel(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/scheduling/earliest-pickup", {
          params: { duration_minutes: Math.round(duration), buffer_minutes: 10 },
        });
        if (stop) return;
        const ts = data?.timestamp ? new Date(data.timestamp) : null;
        if (!ts) return;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const base = new Date(ts); base.setHours(0, 0, 0, 0);
        const sameDay = base.getTime() === today.getTime();
        const hh = ts.getHours(), mm = ts.getMinutes();
        const ampm = hh >= 12 ? "PM" : "AM";
        const h12 = ((hh + 11) % 12) + 1;
        const startLabel = `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
        // 15-minute transportation window
        const endTs = new Date(ts.getTime() + 15 * 60 * 1000);
        const eh = endTs.getHours(), em = endTs.getMinutes();
        const eAmpm = eh >= 12 ? "PM" : "AM";
        const eh12 = ((eh + 11) % 12) + 1;
        const endLabel = `${eh12}:${String(em).padStart(2, "0")} ${eAmpm}`;
        const time12 = `${startLabel} – ${endLabel}`;
        const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
        const isTomorrow = base.getTime() === tomorrow.getTime();
        const dayLabel = sameDay ? "Today" : isTomorrow ? "Tomorrow" : ts.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
        setEarliestLabel(`${dayLabel} ${time12}`);
      } catch (_) { /* noop */ }
    })();
    return () => { stop = true; };
  }, [duration]);

  // Progressive CTA — the schedule slide always reads "Slide to schedule
  // ride" so the rider knows what they're about to commit to. Cycle-
  // specific phrasing happens inside the payment sheet (next stage). On
  // a successful slide, the handle locks at "Ride scheduled" while the
  // payment sheet opens — giving the rider a moment of confirmation
  // before the next surface paints.
  const ctaLabel = "Continue to Payment";
  const ctaConfirmedLabel = "Scheduling…";
  // Return window must be strictly AFTER pickup window on the same
  // service day. We compare start-minutes; any bad order is caught
  // here BEFORE the CTA can fire.
  const windowsInOrder = useMemo(() => {
    if (!pickupWindow || !returnWindow) return true;
    const toMin = (w) => {
      const [h, m] = w.slice(0, 5).split(":").map((n) => parseInt(n, 10));
      return (Number.isNaN(h) ? 0 : h) * 60 + (Number.isNaN(m) ? 0 : m);
    };
    return toMin(returnWindow) > toMin(pickupWindow);
  }, [pickupWindow, returnWindow]);
  const windowError = !windowsInOrder ? "Return window must be after pickup window on the same day." : null;
  const slideDisabled = !currentTier || days.length === 0 || !user || !windowsInOrder;

  const slideAndPay = () => {
    if (slideDisabled) {
      if (windowError) toast.error(windowError);
      return;
    }
    onSubmit();
  };

  return (
    <section data-testid="page-plan" className="flex-1 relative">
      {/* Map stays in the background — the booking surface evolves on top of it. */}
      <div
        className="map-container fixed inset-0"
        style={{ height: "100dvh", minHeight: "-webkit-fill-available", zIndex: 0 }}
        data-testid="plan-map"
      >
        <RouteMap
          center={userLoc}
          polyline={polyline}
          userLocation={hasUserLoc ? userLoc : null}
          fallbackSrc={fallbackMapSrc}
          hideRecenter
          recenterAnchor="top"
        />
      </div>

      {/* Top legibility gradient + back chip */}
      <div className="fixed top-0 inset-x-0 h-32 pointer-events-none fade-top" style={{ zIndex: 5 }} aria-hidden="true" />
      <div
        className="fixed top-0 inset-x-0 px-4 pointer-events-none"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 72px)", zIndex: 20 }}
      >
        <div className="max-w-2xl mx-auto flex items-center gap-3 pointer-events-auto">
          <button
            onClick={onBack}
            className="h-10 px-3 rounded-full bg-[#0A1128]/95 text-[#F9F7F4] backdrop-blur-xl border border-white/10 shadow-xl inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-semibold"
            data-testid="plan-back"
          >
            <ArrowLeft size={14} /> Edit route
          </button>
          <div className="flex-1 truncate text-[11px] tracking-[0.18em] uppercase font-semibold text-[#0A1128]/85 bg-white/85 backdrop-blur px-3 h-10 rounded-full inline-flex items-center border border-white/40 shadow-sm">
            <span className="truncate">{(pickup || "").split(",")[0]} → {(destination || "").split(",")[0]}</span>
          </div>
        </div>
      </div>

      {/* THE PLAN CARD — explicitly bounded by both `top` (just below the
          Edit-route header) and `bottom` (above the bottom nav) so the
          card NEVER overlaps the header chip on any viewport size.  Old
          version relied solely on `max-h: 70vh` which collided with the
          header on tall iPhone Pro viewports. */}
      <div
        className="fixed left-1/2 -translate-x-1/2 pointer-events-none"
        style={{
          top: "calc(env(safe-area-inset-top, 0px) + 148px)",
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 80px)",
          zIndex: 30,
          width: "min(640px, calc(100vw - 24px))",
        }}
        data-testid="plan-surface"
      >
        <div
          className="pointer-events-auto bg-[#0A1128]/95 backdrop-blur-xl border border-white/10 shadow-2xl text-[#F9F7F4] overflow-hidden rounded-[28px] h-full flex flex-col"
          style={{
            transition: "all 380ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
          data-state={billing}
        >
          <div className="flex-1 overflow-y-auto wheel-scroll">

            {/* ================= §1 SCHEDULE ================= */}
            <section className="px-5 pt-5 pb-5 space-y-4" data-testid="plan-section-schedule">
              <div className="flex items-center justify-between">
                <SectionTitle number="1" label="Schedule" />
                <span className="text-[10px] tracking-[0.18em] uppercase text-[#F9F7F4]/55" data-testid="plan-days-summary">
                  {days.length === 0 ? "Pick your days"
                   : days.length === 1 ? "1 day selected"
                   : `${days.length} days selected`}
                </span>
              </div>

              {/* Days grid — selected chip is visually stronger (scale + bold). */}
              <div className="grid grid-cols-7 gap-1.5" data-testid="plan-day-grid">
                {DAYS.map((d) => {
                  const active = days.includes(d);
                  return (
                    <button
                      key={d}
                      onClick={() => toggleDay(d)}
                      className={`h-12 rounded-xl text-[13px] tracking-[0.14em] uppercase transition-all duration-200 ${
                        active
                          ? "bg-[#ed6500] text-white font-bold scale-[1.04] shadow-md shadow-black/20"
                          : "bg-white/5 text-[#F9F7F4]/70 font-semibold hover:bg-white/10"
                      }`}
                      data-testid={`plan-day-${d}`}
                      aria-pressed={active}
                    >
                      {d.slice(0, 1)}
                    </button>
                  );
                })}
              </div>

              {/* Time inputs — organized into two contextual groups so the
                  rider can tell at a glance which leg of the commute each
                  dial represents.  "Depart by" replaces "Return" — it's
                  the work-leaving time (pickup time FROM destination). */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <div
                    className="text-[9.5px] tracking-[0.22em] uppercase font-semibold text-[#F9F7F4]/55"
                    data-testid="plan-time-arrival-context"
                  >
                    To work
                  </div>
                  <TimeChip
                    label="Arrive by"
                    value={arrival}
                    active={openTime === "arrival"}
                    onTap={() => { setTimesTouched(true); setOpenTime(openTime === "arrival" ? null : "arrival"); }}
                    testId="plan-time-arrival"
                  />
                </div>
                <div className="space-y-1.5">
                  <div
                    className="text-[9.5px] tracking-[0.22em] uppercase font-semibold text-[#F9F7F4]/55"
                    data-testid="plan-time-return-context"
                  >
                    From work
                  </div>
                  <TimeChip
                    label="Depart by"
                    value={ret}
                    active={openTime === "return"}
                    onTap={() => { setTimesTouched(true); setOpenTime(openTime === "return" ? null : "return"); }}
                    testId="plan-time-return"
                  />
                </div>
              </div>
              {/* Pickup hint — strictly one-or-the-other per spec.
                  Untouched  →  "Recommended pickup: Today X:XX"
                  Touched    →  "Recommended pickup: X:XX / Based on your arrival time" */}
              {!timesTouched && earliestLabel ? (
                <div data-testid="plan-pickup-hint-earliest">
                  <div className="text-[12px] text-[#F9F7F4]/80">
                    Recommended pickup:{" "}
                    <span className="font-semibold text-[#F9F7F4]">{earliestLabel}</span>
                  </div>
                  <div className="text-[10.5px] text-[#F9F7F4]/50 mt-0.5">
                    Based on your arrival time
                  </div>
                </div>
              ) : timesTouched && pickupTime ? (
                <div data-testid="plan-pickup-hint">
                  <div className="text-[12px] text-[#F9F7F4]/80">
                    Recommended pickup window:{" "}
                    <span className="font-semibold text-[#F9F7F4]">{formatPickupWindow(pickupTime)}</span>
                  </div>
                  <div className="text-[10.5px] text-[#F9F7F4]/50 mt-0.5">
                    Based on your arrival time
                  </div>
                </div>
              ) : null}

              {/* Inline wheel popover for the default pair. */}
              <div
                className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
                style={{
                  maxHeight: openTime && !openTime.startsWith("override:") ? 220 : 0,
                  opacity: openTime && !openTime.startsWith("override:") ? 1 : 0,
                }}
              >
                <div data-testid="plan-time-wheel">
                  {openTime === "arrival" && (
                    <TimeTriPicker value={arrival} onChange={(v) => { setTimesTouched(true); setArrival(v); }} testId="plan-arrival-wheel" />
                  )}
                  {openTime === "return" && (
                    <TimeTriPicker value={ret} onChange={(v) => { setTimesTouched(true); setRet(v); }} testId="plan-return-wheel" />
                  )}
                </div>
              </div>

              {/* Flexible schedule toggle — new copy per spec. */}
              {days.length > 0 && (
                <PerDayOverridesPanel
                  days={days}
                  arrival={arrival}
                  ret={ret}
                  dayOverrides={dayOverrides || {}}
                  setDayOverrides={setDayOverrides}
                  openTime={openTime}
                  setOpenTime={setOpenTime}
                />
              )}
            </section>

            {/* ================= §2 PRICING ================= */}
            <section
              className="px-5 py-5 space-y-3 border-t border-white/10"
              data-testid="plan-section-pricing"
            >
              <SectionTitle number="2" label="Pricing" />

              {/* Tier — name + informative tagline (sourced from backend PRICING_TIERS). */}
              {currentTier && (
                <div className="flex items-baseline justify-between" data-testid="plan-tier-row">
                  <div className="min-w-0">
                    <div className="font-serif text-[15px] text-[#F9F7F4]" data-testid="plan-tier-name">
                      {currentTier.name}
                    </div>
                    <div className="text-[11px] text-[#F9F7F4]/55 mt-0.5" data-testid="plan-tier-tagline">
                      {currentTier.tagline || "Best for daily commutes"}
                    </div>
                  </div>
                  <div className="text-[10px] text-[#F9F7F4]/45 shrink-0 tabular-nums">
                    {(distance || 0).toFixed(1)} mi · {duration || 0} min
                  </div>
                </div>
              )}

              {/* Payment type toggle — surfaces only for multi-day bookings.
                  Single-day rides are always one-time; for 2+ days the rider
                  MUST be able to choose between paying once or starting a
                  weekly subscription.  First tap locks in the choice via
                  `cycleUserPicked` so subsequent day-count edits never
                  override the rider. */}
              {days.length >= 2 && (
                <div
                  className="grid grid-cols-2 gap-2 pt-1"
                  role="radiogroup"
                  aria-label="Payment type"
                  data-testid="plan-paytype-toggle"
                >
                  {[
                    { id: "payg",   label: "One-time",    sub: "Pay for this week only" },
                    { id: "weekly", label: "Subscription", sub: "Auto-renew weekly" },
                  ].map((opt) => {
                    const selected = opt.id === "payg" ? cycle === "payg" : cycle !== "payg";
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => {
                          setCycleUserPicked(true);
                          setCycle(opt.id === "payg" ? "payg" : (cycle !== "payg" ? cycle : "weekly"));
                        }}
                        className={[
                          "text-left px-3 py-2.5 border transition-all",
                          selected
                            ? "bg-[#ed6500] text-white border-[#ed6500] shadow-sm"
                            : "bg-white/5 text-[#F9F7F4] border-white/20 hover:border-white/50",
                        ].join(" ")}
                        data-testid={`plan-paytype-${opt.id}`}
                        data-selected={selected}
                      >
                        <div className="text-[10.5px] tracking-widest uppercase font-semibold">
                          {opt.label}
                        </div>
                        <div className={`text-[10px] mt-0.5 ${selected ? "text-white/85" : "text-[#F9F7F4]/55"}`}>
                          {opt.sub}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Headline price — $/day + round-trip copy + weekly roll-up. */}
              <div data-testid="plan-pricing" className="pt-1">
                <div
                  className="font-serif text-[28px] leading-none font-semibold text-[#ed6500] tabular-nums"
                  data-testid="plan-daily-price"
                >
                  ${dailyRate.toFixed(2)}
                  <span className="text-[13px] text-[#F9F7F4]/60 font-normal ml-1">/day</span>
                </div>
                <div className="text-[12px] text-[#F9F7F4]/65 mt-1">
                  Includes pickup and return ride
                </div>

                {/* Dynamic weekly pricing context — surfaces only when 2+
                    days are selected. Recomputes live from the user's
                    selection: dailyRate * days.length. Sits between the
                    daily headline and the cycle total so the rider sees
                    the implied weekly burden without hunting for it. */}
                {days.length >= 2 && (
                  <div
                    className="text-[11px] text-[#F9F7F4]/65 mt-1.5 tabular-nums"
                    data-testid="plan-weekly-context"
                  >
                    ≈ ${(dailyRate * days.length).toFixed(2)} per week (based on {days.length} days)
                  </div>
                )}

                {billing === "weekly" ? (
                  <>
                    <div className="text-[13px] text-[#F9F7F4]/85 mt-3 tabular-nums flex items-baseline" data-testid="plan-weekly-total">
                      <PriceOdometer value={weeklyTotal} testid="plan-weekly-total-value" />
                      <span className="text-[#F9F7F4]/55 text-[11px] font-normal ml-1">/week</span>
                    </div>
                    <div className="text-[10.5px] text-[#F9F7F4]/50 mt-0.5">
                      Renews weekly · Cancel anytime
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-[13px] text-[#F9F7F4]/85 mt-3 tabular-nums flex items-baseline" data-testid="plan-onetime-total">
                      <PriceOdometer value={oneTimeTotal} testid="plan-onetime-total-value" />
                      <span className="text-[#F9F7F4]/55 text-[11px] font-normal ml-1">total</span>
                    </div>
                    <div className="text-[10.5px] text-[#F9F7F4]/50 mt-0.5">
                      One-time charge · No subscription
                    </div>
                  </>
                )}

                {firstRideDiscount > 0 && billing === "weekly" && isWelcome && (
                  <div className="text-[11px] text-[#ed6500] mt-2" data-testid="plan-welcome-discount">
                    Welcome offer · −${firstRideDiscount.toFixed(2)} on your first week
                  </div>
                )}
              </div>
            </section>

            {/* ================= §3 ACTION ================= */}
            <section
              className="px-5 pt-4 pb-5 space-y-3 border-t border-white/10"
              data-testid="plan-section-action"
            >
              {/* Trust strip — same three quiet proof points used on the
                  route card, echoed here for reassurance at commit. */}
              <div
                className="flex items-center justify-between gap-1.5 text-[9.5px] text-[#F9F7F4]/70 tracking-wide whitespace-nowrap"
                data-testid="plan-trust-strip"
              >
                <span className="inline-flex items-center gap-1">
                  <Check size={10} className="text-[#ed6500] shrink-0" />
                  <span>Same daily price</span>
                </span>
                <span className="inline-flex items-center gap-1">
                  <Check size={10} className="text-[#ed6500] shrink-0" />
                  <span>No surge</span>
                </span>
                <span className="inline-flex items-center gap-1">
                  <Check size={10} className="text-[#ed6500] shrink-0" />
                  <span>Pickup + return</span>
                </span>
              </div>

              <div data-testid="plan-slide-wrap">
                <button
                  type="button"
                  onClick={slideAndPay}
                  disabled={slideDisabled || submitting}
                  className="w-full h-14 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[13px] tracking-[0.24em] uppercase font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="plan-continue-button"
                >
                  {submitting ? ctaConfirmedLabel : ctaLabel}
                </button>
                {windowError && (
                  <div
                    className="text-center text-[12px] text-[#ff7b1f] mt-2 leading-relaxed"
                    role="alert"
                    data-testid="plan-window-error"
                  >
                    {windowError}
                  </div>
                )}
                <div
                  className="text-center text-[11px] text-[#F9F7F4]/55 mt-2"
                  data-testid="plan-cta-subtext"
                >
                  {!user
                    ? "Sign in to continue — your draft is saved"
                    : "You’ll confirm schedule and payment next"}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </section>
  );
}

function Label({ children }) {
  return (
    <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#F9F7F4]/55">
      {children}
    </div>
  );
}

/** Compact numbered section heading — "1 · SCHEDULE" etc. */
function SectionTitle({ number, label }) {
  return (
    <div className="inline-flex items-center gap-2" data-testid={`plan-section-title-${number}`}>
      <span className="w-5 h-5 rounded-full bg-[#ed6500]/15 text-[#ed6500] text-[10px] font-bold inline-flex items-center justify-center">
        {number}
      </span>
      <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#F9F7F4]/75">
        {label}
      </span>
    </div>
  );
}

function TimeChip({ label, value, active, onTap, testId }) {
  return (
    <button
      type="button"
      onClick={onTap}
      className={`h-12 px-3 rounded-2xl border text-left transition-colors ${
        active ? "bg-[#ed6500] border-[#ed6500] text-white" : "bg-white/5 border-white/10 text-[#F9F7F4]/85 hover:bg-white/10"
      }`}
      data-testid={testId}
      aria-expanded={active}
    >
      <div className={`text-[9px] tracking-[0.22em] uppercase font-semibold ${active ? "text-white/85" : "text-[#F9F7F4]/55"}`}>{label}</div>
      <div className="font-serif text-base leading-none">{formatTime12(value || "")}</div>
    </button>
  );
}

/* 5-minute wheel — scroll-snap column with center band selection. */
function FiveMinuteWheel({ value, onChange, startHour = 6, endHour = 22 }) {
  const ITEM_H = 36;
  const wrapRef = useRef(null);
  // Build options every 5 minutes between startHour:00 and endHour:55.
  const options = useMemo(() => {
    const out = [];
    for (let h = startHour; h <= endHour; h++) {
      for (let m = 0; m < 60; m += 5) {
        out.push(`${pad2(h)}:${pad2(m)}`);
      }
    }
    return out;
  }, [startHour, endHour]);

  const indexOf = (v) => Math.max(0, options.indexOf(v));
  const settleTimer = useRef(null);

  useEffect(() => {
    const idx = indexOf(value);
    if (wrapRef.current) wrapRef.current.scrollTop = idx * ITEM_H;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options]);

  const onScroll = () => {
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      if (!wrapRef.current) return;
      const idx = Math.round(wrapRef.current.scrollTop / ITEM_H);
      const clamped = Math.max(0, Math.min(options.length - 1, idx));
      const next = options[clamped];
      if (next && next !== value) onChange(next);
    }, 90);
  };

  return (
    <div className="relative" data-testid="plan-time-wheel-wrap">
      <div
        ref={wrapRef}
        onScroll={onScroll}
        className="wheel-scroll relative h-[144px] overflow-y-auto"
        style={{ scrollSnapType: "y mandatory" }}
      >
        {/* spacer top so first item can land in centre band */}
        <div style={{ height: ITEM_H * 2 }} aria-hidden="true" />
        {options.map((t, i) => {
          const selected = t === value;
          return (
            <div
              key={t}
              className={`flex items-center justify-center text-base font-serif transition-all ${
                selected ? "text-[#F9F7F4] text-2xl" : "text-[#F9F7F4]/40"
              }`}
              style={{ height: ITEM_H, scrollSnapAlign: "center" }}
              onClick={() => {
                if (wrapRef.current) wrapRef.current.scrollTo({ top: i * ITEM_H, behavior: "smooth" });
                onChange(t);
              }}
            >
              {formatTime12(t)}
            </div>
          );
        })}
        <div style={{ height: ITEM_H * 2 }} aria-hidden="true" />
      </div>
      {/* selection band */}
      <div
        className="pointer-events-none absolute inset-x-2 top-1/2 -translate-y-1/2 h-9 rounded-xl border border-[#ed6500]/40 bg-[#ed6500]/8"
        aria-hidden="true"
      />
    </div>
  );
}


/* ===========================================================================
   PerDayOverridesPanel — flex-schedule per-day arrival/return overrides
   ===========================================================================
   For riders whose Tuesday class ends earlier or whose Friday lab runs late.
   Default times still apply to every day; tapping a day's "Custom" toggle
   reveals two TimeChips that, when changed, store the override in
   dayOverrides[day]. Removing the override (Reset) restores the default. */
function PerDayOverridesPanel({ days, arrival, ret, dayOverrides, setDayOverrides, openTime, setOpenTime }) {
  const [expanded, setExpanded] = useState(false);
  const overrideCount = Object.keys(dayOverrides || {}).length;

  const setDayTime = (day, kind, value) => {
    const updated = { ...(dayOverrides || {}) };
    const obj = { ...(updated[day] || {}) };
    obj[kind] = value;
    // If the override matches default, clear it so we don't persist a no-op.
    const defaultMatch = (kind === "arrival_time" && value === arrival) || (kind === "return_time" && value === ret);
    if (defaultMatch) delete obj[kind];
    if (Object.keys(obj).length === 0) delete updated[day];
    else updated[day] = obj;
    setDayOverrides(updated);
  };

  const reset = (day) => {
    const updated = { ...(dayOverrides || {}) };
    delete updated[day];
    setDayOverrides(updated);
  };

  return (
    <div data-testid="plan-day-overrides">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-2xl bg-white/5 border border-white/10 text-[#F9F7F4]/85 hover:bg-white/10 transition-colors"
        aria-expanded={expanded}
        data-testid="plan-overrides-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles size={13} className="text-[#ed6500] shrink-0" />
          <div className="min-w-0 text-left">
            <div className="text-[12px] font-semibold text-[#F9F7F4]">Flexible schedule</div>
            <div className="text-[10.5px] text-[#F9F7F4]/55">Set different times per day</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {overrideCount > 0 && (
            <span className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#ed6500]">
              {overrideCount} custom
            </span>
          )}
          <ChevronDown size={14} className="text-[#F9F7F4]/55 transition-transform" style={{ transform: expanded ? "rotate(180deg)" : "rotate(0)" }} />
        </div>
      </button>

      <div
        className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
        style={{ maxHeight: expanded ? 9999 : 0, opacity: expanded ? 1 : 0 }}
      >
        <div className="pt-3 space-y-2">
          {days.map((d) => {
            const o = dayOverrides?.[d] || {};
            const a = o.arrival_time || arrival;
            const r = o.return_time || ret;
            const isCustom = !!(o.arrival_time || o.return_time);
            const arrKey = `override:${d}:arrival_time`;
            const retKey = `override:${d}:return_time`;
            return (
              <div
                key={d}
                className={`p-3 rounded-2xl border ${isCustom ? "bg-[#ed6500]/10 border-[#ed6500]/30" : "bg-white/5 border-white/10"}`}
                data-testid={`plan-override-row-${d}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#F9F7F4]/85">{d}</span>
                  {isCustom ? (
                    <button
                      type="button"
                      onClick={() => reset(d)}
                      className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5c97f7] hover:text-[#7eb1ff]"
                      data-testid={`plan-override-reset-${d}`}
                    >
                      Reset to default
                    </button>
                  ) : (
                    <span className="text-[10px] tracking-[0.18em] uppercase text-[#F9F7F4]/45">Default</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <TimeChip
                    label="Arrive by"
                    value={a}
                    active={openTime === arrKey}
                    onTap={() => setOpenTime(openTime === arrKey ? null : arrKey)}
                    testId={`plan-override-${d}-arrival`}
                  />
                  <TimeChip
                    label="Depart by"
                    value={r}
                    active={openTime === retKey}
                    onTap={() => setOpenTime(openTime === retKey ? null : retKey)}
                    testId={`plan-override-${d}-return`}
                  />
                </div>
                {/* Inline wheel for this day's open chip */}
                <div
                  className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
                  style={{
                    maxHeight: openTime === arrKey || openTime === retKey ? 220 : 0,
                    opacity: openTime === arrKey || openTime === retKey ? 1 : 0,
                  }}
                >
                  <div className="pt-3" data-testid={`plan-override-${d}-wheel`}>
                    {openTime === arrKey && (
                      <TimeTriPicker value={a} onChange={(v) => setDayTime(d, "arrival_time", v)} testId={`plan-override-${d}-arrival-wheel`} />
                    )}
                    {openTime === retKey && (
                      <TimeTriPicker value={r} onChange={(v) => setDayTime(d, "return_time", v)} testId={`plan-override-${d}-return-wheel`} />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
